"use client";

/* ===== easy-tedu · media screens (videos / upload / playlists / pairing) ===== */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { tkdTitle, tkdPlaylist, tkdForm, tkdSearchText, tkdRefGroup, tkdRefSub, tkdTechKind } from "@/lib/tkd-terms";
import * as tus from "tus-js-client";
import jsQR from "jsqr";
import { I } from "./icons";
import { AppBar, Poster, Track, SheetShell, Segmented, ScrollSolidify, useSegRefLabel, useData, playlistDuration, useGripReorder, GripHandle } from "./ui";
import { fmt, isReferenceVideo, addedPlaySet, type VideoView, type PlaylistView, type DeviceView, type RecentCast } from "./types";
import { GlassGroup, GlassRow, MediaSlot, SecLabel } from "./glass-list";
import { FormDrill, type MasterForm } from "./setlist-screen";
import type { MasterPlayVideo, DrillPlayOpt } from "@/lib/realtime-types";
import { canDownscale, maybeDownscale } from "@/lib/downscale";
import { perf } from "@/lib/perf";
import { parseSegment, segmentKindOf } from "@/lib/ref-segment";
import { showsBasics } from "@/lib/browse-meta";
import { usePrefetch, prefetchSet, cancelPrefetch, setPlan, useCachedKeys, isCached, type SetItem, type SetPlan } from "@/lib/mirror-cache";
import { useMirrorQuality } from "@/lib/mirror-quality";
import { RANK_BASICS } from "@/lib/rank-limit";

/* ---------- Video library ---------- */
/* HLS 변환 상태 칩 — 전송/변환 중엔 진행률, 완료/실패 표시 (none 은 미표시) */
function HlsChip({ v }: { v: VideoView }) {
  const tm = useTranslations("control.media");
  const h = v.hls;
  if (!h || h.state === "none") return null;
  if (h.state === "ready") return (
    <span className="chip chip--accent" style={{ height: 22, fontSize: 10.5, fontWeight: 700, gap: 3, padding: "0 9px 0 7px", letterSpacing: ".02em" }}>
      <I.check size={11} sw={2.8} />HLS
    </span>
  );
  if (h.state === "failed") return <span className="chip chip--warn" style={{ height: 22, fontSize: 11 }} title={h.error}>{tm("hls.failed")}</span>;
  // 퍼센트는 실제 중간 진행률(1~99)이 들어올 때만 — 고속 변환의 "0% 멈춤·100% 직전 점프" 깜빡임 방지
  const label =
    h.state === "queued" ? tm("hls.queued")
    : h.state === "uploading" ? tm("hls.uploading")
    : h.progress >= 1 && h.progress < 100 ? tm("hls.convertingPct", { pct: Math.round(h.progress) })
    : tm("hls.converting");
  return (
    <span className="chip chip--off" style={{ height: 22, fontSize: 11 }}>
      <span className="spin" style={{ display: "inline-flex" }}><I.spinner size={11} sw={2.4} /></span>
      {label}
    </span>
  );
}

/* ---------- Video tab + search (영상 라이브러리 / 재생목록 영상 추가 공용) ----------
   '내 영상'(직접 업로드) / '기본·품새'(레퍼런스 시드) 두 탭으로 나누고 제목 검색을 더한다.
   라이브러리·재생목록 추가 시트 양쪽에서 같은 동작을 보장하려 훅으로 분리. */
type VideoTab = "mine" | "ref";

function useVideoFilter(videos: VideoView[], initialTab: VideoTab = "ref") {
  const [tab, setTab] = useState<VideoTab>(initialTab); // 기본=기본·품새(콘텐츠 풍부) — 신규 도장 빈 화면 방지·전역 통일
  const [q, setQ] = useState("");
  const locale = useLocale();
  const mine = videos.filter((v) => !isReferenceVideo(v));
  const ref = videos.filter(isReferenceVideo);
  const base = tab === "mine" ? mine : ref;
  const query = q.trim().toLowerCase();
  const filtered = query ? base.filter((v) => tkdSearchText(v.title, locale).includes(query)) : base;
  return { tab, setTab, q, setQ, query, filtered, counts: { mine: mine.length, ref: ref.length } };
}

function VideoFilterBar({ tab, setTab, q, setQ, counts }: {
  tab: VideoTab;
  setTab: (t: VideoTab) => void;
  q: string;
  setQ: (v: string) => void;
  counts: { mine: number; ref: number };
}) {
  const tm = useTranslations("control.media");
  const segRef = useSegRefLabel();
  // 검색은 늘 보인다 — 예전엔 세그먼트 오른쪽에 아이콘만 외따로 떠 있다가 눌러야 펼쳐졌는데,
  // 그건 이 시트에만 있던 예외였다. 다른 화면(내 영상 시트·재생목록 담기)은 전부 상시 검색바(.vsearch)다.
  return (
    // 세그먼트와 검색바는 '목록을 좁히는 도구' 하나의 덩어리다 — 사이를 좁혀(6px) 그렇게 읽히게 한다.
    <div className="col" style={{ gap: 6 }}>
      <Segmented value={tab} onChange={setTab} options={[
        { value: "ref", label: `${segRef} ${counts.ref}` },
        { value: "mine", label: tm("filter.myVideos", { count: counts.mine }) },
      ]} />
      <div className="vsearch">
        <I.search size={17} className="dim" />
        <input className="vsearch__input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={tm("filter.searchTitle")} inputMode="search" />
        {q && <button className="vsearch__clear" onClick={() => setQ("")} aria-label={tm("common.clearSearch")}><I.x size={14} sw={2.2} /></button>}
      </div>
    </div>
  );
}

/* ---------- 영상 한 줄(라이브러리 카드) — 평면/그룹 양쪽에서 재사용 ---------- */
/* 이 영상은 관리 앱에 담겨 있다 — 목록 줄에 조용히 한 마디.
   🛑 눈에 띄는 배지(색·테두리)로 만들지 말 것: 열 줄 중 아홉에 붙는 표시라 목록이 시끄러워진다.
      시간·'숨김'·'내가 추가'와 같은 급의 회색 메타 한 조각으로 둔다.
   ⭐ 이걸 붙인 이유(2026-08-28 사용자): "필요한 상황인데 저장이 되어 있는지 아닌지는 알아야 할 거 아니야".
      목록 단위(부제에 n/N)로 하자던 안은 반려됐다 — 같은 영상이 여러 재생목록에 겹쳐 들어가 있어서
      '이 목록은 준비됨'이 영상 하나하나의 사실을 못 말한다(예: 내가 만든 목록에 태백 정면 1편만 담아 두면
      태백 화면은 18편 중 1편만 담긴 상태다). 담김은 목록이 아니라 **파일 하나하나에 붙는다**. */
function SavedMark({ label }: { label: string }) {
  return (
    <span className="row gap-3" style={{ alignItems: "center", color: "var(--text-3)", fontWeight: 700 }}>
      <I.download size={12} />{label}
    </span>
  );
}

function VideoRow({ v, onClick, onMenu, delay = 0 }: {
  v: VideoView; onClick: () => void; onMenu?: (v: VideoView) => void; delay?: number;
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const saved = isCached(useCachedKeys(), v);
  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 15, padding: "11px 16px",
      animationDelay: `${delay}ms`, cursor: "pointer", background: "transparent",
    }}>
      <MediaSlot><Poster video={v} w="100%" h="100%" showLabel={false} radius={0} /></MediaSlot>
      <div className="col gap-4" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-.01em", color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(v.title, locale)}</div>
        <div className="row gap-8 muted" style={{ fontSize: 12.5 }}>
          <span className="row gap-3 tnum" style={{ alignItems: "center" }}><I.clock size={12.5} />{fmt(v.durationSec)}</span>
          {saved && <SavedMark label={tm("offline.saved")} />}
        </div>
        {v.hls?.state === "processing" && (
          v.hls.progress >= 1 && v.hls.progress < 100
            ? <Track value={v.hls.progress} h={4} />
            : <Track value={0} h={4} indeterminate />
        )}
        {(v.hls?.state === "queued" || v.hls?.state === "uploading") && <Track value={0} h={4} indeterminate color="var(--surface-3)" />}
      </div>
      <div className="col gap-6" style={{ alignItems: "flex-end" }}>
        {v.status !== "READY" && <span className="chip chip--warn" style={{ height: 24 }}>{v.status}</span>}
        <HlsChip v={v} />
        {onMenu && <button className="iconbtn iconbtn--plain" style={{ width: 30, height: 26, color: "var(--text-3)" }} onClick={(e) => { e.stopPropagation(); onMenu(v); }} aria-label={tm("common.more")}><I.dots size={18} /></button>}
      </div>
    </div>
  );
}

/* ---------- 날짜(년/월/주차) 그룹화 — '내 영상' 라이브러리 ----------
   평면 한 덩어리를 업로드 시각(createdAt) 기준 년 > 월 > 주차 접이식으로.
   주차 = 그 달의 몇째 주(월요일 시작, 1일이 든 주 = 1주차) — 벽걸이 달력 감각과 동일. */
function weekOfMonth(d: Date): number {
  const firstDow = (new Date(d.getFullYear(), d.getMonth(), 1).getDay() + 6) % 7; // 1일 요일(월=0..일=6)
  return Math.floor((d.getDate() - 1 + firstDow) / 7) + 1;
}
const EN_MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function weekBounds(y: number, m1: number, w: number): [number, number] {
  const m = m1 - 1;
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const start = Math.max(1, (w - 1) * 7 - firstDow + 1);
  const end = Math.min(days, w * 7 - firstDow);
  return [start, end];
}
type WeekG = { w: number; start: number; end: number; videos: VideoView[] };
type MonthG = { m: number; weeks: WeekG[]; count: number };
type YearG = { y: number; months: MonthG[]; count: number };
function groupByDate(videos: VideoView[]): YearG[] {
  const years: YearG[] = []; // videos 는 최신순(API createdAt desc) → 순회 순서가 곧 표시 순서
  for (const v of videos) {
    const d = new Date(v.createdAt);
    const y = d.getFullYear(), m = d.getMonth() + 1, w = weekOfMonth(d);
    let yg = years.find((x) => x.y === y);
    if (!yg) { yg = { y, months: [], count: 0 }; years.push(yg); }
    yg.count++;
    let mg = yg.months.find((x) => x.m === m);
    if (!mg) { mg = { m, weeks: [], count: 0 }; yg.months.push(mg); }
    mg.count++;
    let wg = mg.weeks.find((x) => x.w === w);
    if (!wg) { const [start, end] = weekBounds(y, m, w); wg = { w, start, end, videos: [] }; mg.weeks.push(wg); }
    wg.videos.push(v);
  }
  return years;
}
const wKey = (y: number, m: number, w: number) => `y${y}m${m}w${w}`;

/* 년/월/주차 휠(드럼) 피커 한 칸 — iOS식 스크롤 선택. CSS scroll-snap 으로 굴리고, 멈춘 위치를 읽는다. */
const WHEEL_ITEM_H = 40;
function WheelCol({ items, index, onIndex }: { items: string[]; index: number; onIndex: (i: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  // 외부 index 변경 또는 항목 교체(캐스케이드) 시 그 위치로 정렬
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const want = Math.min(Math.max(index, 0), Math.max(items.length - 1, 0)) * WHEEL_ITEM_H;
    if (Math.abs(el.scrollTop - want) > 1) el.scrollTop = want;
  }, [index, items.length]);
  const onScroll = () => {
    const el = ref.current; if (!el) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const i = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / WHEEL_ITEM_H)));
      const want = i * WHEEL_ITEM_H;
      if (Math.abs(el.scrollTop - want) > 1) el.scrollTo({ top: want, behavior: "smooth" });
      if (i !== index) onIndex(i);
    }, 120);
  };
  return (
    <div ref={ref} onScroll={onScroll} style={{
      flex: 1, minWidth: 0, height: WHEEL_ITEM_H * 5, overflowY: "auto",
      scrollSnapType: "y mandatory", scrollbarWidth: "none",
    }}>
      <div style={{ height: WHEEL_ITEM_H * 2 }} />
      {items.map((it, i) => (
        <div key={i} style={{
          height: WHEEL_ITEM_H, scrollSnapAlign: "center", display: "grid", placeItems: "center",
          fontSize: 15.5, whiteSpace: "nowrap", padding: "0 4px",
          fontWeight: i === index ? 800 : 600, color: i === index ? "var(--text-1)" : "var(--text-3)",
        }}>{it}</div>
      ))}
      <div style={{ height: WHEEL_ITEM_H * 2 }} />
    </div>
  );
}

/* '내 영상' — 사진앱식 스크롤. 접지 않고 쭉 흐르며, 주차마다 sticky 날짜 헤더가 붙어
   "지금 어느 주차를 보는지" 알려준다(그 자체가 전체보기).
   + 주차 헤더 끝의 ▾ 를 탭하면 년/월/주차 휠(드럼)이 떠 그 주차로 바로 이동(추가). */
function GroupedLibrary({ videos, onPlay, onMenu }: {
  videos: VideoView[]; onPlay: (ids: string[], index: number, title: string) => void; onMenu: (v: VideoView) => void;
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const idList = useMemo(() => videos.map((v) => v.id), [videos]);
  const idxMap = useMemo(() => new Map(idList.map((id, i) => [id, i] as const)), [idList]);
  // 주차 그룹을 표시 순서(최신순)대로 평탄화 — 각 그룹 = '년 월 · N주차 (범위)' 헤더 + 영상들.
  const groups = useMemo(() => {
    const out: { key: string; label: string; videos: VideoView[] }[] = [];
    for (const yg of groupByDate(videos))
      for (const mg of yg.months)
        for (const wg of mg.weeks)
          out.push({ key: wKey(yg.y, mg.m, wg.w), label: tm("library.weekLabel", { y: yg.y, m: mg.m, mName: EN_MON[mg.m - 1], w: wg.w, start: wg.start, end: wg.end }), videos: wg.videos });
    return out;
  }, [videos]);
  const tree = useMemo(() => groupByDate(videos), [videos]); // 년>월>주 (휠 캐스케이드용)

  const rootRef = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState<string>("");
  const [wheel, setWheel] = useState(false);
  const [yi, setYi] = useState(0);
  const [mi, setMi] = useState(0);
  const [wi, setWi] = useState(0);
  const multi = groups.length > 1;

  const jumpTo = (key: string) =>
    document.getElementById(`wk-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  // 스크롤 따라 '지금 보는 주차' 추적 → 휠 열 때 초기값
  useEffect(() => {
    const sc = rootRef.current?.closest<HTMLElement>(".frame__scroll");
    if (!sc) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // 날짜헤더가 플로팅 상단바 밑(--ctl-hd-h)에 붙으므로, '지금 보는 주차' 판정선도 그 높이 아래로 맞춘다.
        const hdH = parseFloat(sc.style.getPropertyValue("--ctl-hd-h")) || 0;
        const scTop = sc.getBoundingClientRect().top + hdH;
        let cur = groups[0]?.key ?? "";
        for (const g of groups) {
          const el = document.getElementById(`wk-${g.key}`);
          if (!el) continue;
          if (el.getBoundingClientRect().top - scTop <= 12) cur = g.key;
          else break;
        }
        setActiveKey(cur);
      });
    };
    onScroll();
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => { cancelAnimationFrame(raf); sc.removeEventListener("scroll", onScroll); };
  }, [groups]);

  // 헤더 ▾ → 지금 보는 주차로 휠 초기화 후 열기
  const openWheel = () => {
    const mm = activeKey.match(/y(\d+)m(\d+)w(\d+)/);
    let ny = 0, nm = 0, nw = 0;
    if (mm) {
      const y = Number(mm[1]), m = Number(mm[2]), w = Number(mm[3]);
      ny = Math.max(0, tree.findIndex((t) => t.y === y));
      nm = Math.max(0, tree[ny]?.months.findIndex((x) => x.m === m) ?? 0);
      nw = Math.max(0, tree[ny]?.months[nm]?.weeks.findIndex((x) => x.w === w) ?? 0);
    }
    setYi(ny); setMi(nm); setWi(nw); setWheel(true);
  };

  // 캐스케이드 클램프 (년 바꾸면 월/주, 월 바꾸면 주 재계산)
  const years = tree.map((t) => t.y);
  const yC = Math.min(yi, Math.max(years.length - 1, 0));
  const months = tree[yC]?.months ?? [];
  const mC = Math.min(mi, Math.max(months.length - 1, 0));
  const weeks = months[mC]?.weeks ?? [];
  const wC = Math.min(wi, Math.max(weeks.length - 1, 0));

  const go = () => {
    setWheel(false);
    const y = years[yC], m = months[mC]?.m, w = weeks[wC]?.w;
    if (y != null && m != null && w != null) jumpTo(wKey(y, m, w));
  };

  return (
    <div className="col" ref={rootRef}>
      {groups.map((g) => (
        <div key={g.key} id={`wk-${g.key}`} style={{ scrollMarginTop: "calc(var(--ctl-hd-h, 0px) + 6px)" }}>
          {/* 주차 구분 헤더(sticky) — 플로팅 유리 상단바 '밑'에 붙는다(top: 헤더높이). top:0 이면 바 뒤로 숨어
             날짜가 위로 스쳐 사라지므로 --ctl-hd-h(=ScrollSolidify 가 잰 바 아래끝) 만큼 내려 고정. 끝의 ▾ = 주차 휠. */}
          <button onClick={multi ? openWheel : undefined} className="lib-datehd" style={{
            position: "sticky", top: "var(--ctl-hd-h, 0px)", zIndex: 2,
            width: "100%", display: "flex", alignItems: "center", gap: 6, textAlign: "left",
            border: "none", fontFamily: "inherit",
            padding: "10px 2px 8px", fontSize: 12.5, fontWeight: 700, color: "var(--text-2)", letterSpacing: "-0.01em",
            cursor: multi ? "pointer" : "default",
          }}>
            <span>{tkdRefGroup(g.label, locale)}</span>
            {multi && <I.chevD size={15} style={{ marginLeft: "auto", flex: "none", opacity: 0.85 }} />}
          </button>
          <div style={{ paddingBottom: 12 }}>
            <GlassGroup glass inset={97}>
              {g.videos.map((v, i) => (
                <VideoRow key={v.id} v={v} delay={i * 35} onMenu={onMenu} onClick={() => onPlay(idList, idxMap.get(v.id) ?? 0, tm("library.myVideos"))} />
              ))}
            </GlassGroup>
          </div>
        </div>
      ))}

      {/* 년/월/주차 휠(드럼) 피커 — 화면 가운데 모달. 가운데 밴드가 선택값, [이동] 누르면 그 주차로. */}
      {wheel && (
        <>
          <div onClick={() => setWheel(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,.5)" }} />
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%,-50%)", zIndex: 61,
            width: "min(380px,88vw)", background: "var(--surface-1)", border: "1px solid var(--border)",
            borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-pop)", padding: 16,
          }}>
            <div style={{ textAlign: "center", fontSize: 15, fontWeight: 800, marginBottom: 12 }}>{tm("library.jumpToWeek")}</div>
            <div style={{ position: "relative", display: "flex", gap: 6 }}>
              {/* 가운데 선택 밴드 */}
              <div style={{
                position: "absolute", left: 0, right: 0, top: WHEEL_ITEM_H * 2, height: WHEEL_ITEM_H,
                background: "var(--accent-soft)", border: "1px solid var(--accent-line)", borderRadius: "var(--r-sm)", pointerEvents: "none",
              }} />
              <WheelCol items={years.map((y) => tm("library.wheelYear", { y }))} index={yC} onIndex={(i) => { setYi(i); setMi(0); setWi(0); }} />
              <WheelCol items={months.map((m) => tm("library.wheelMonth", { m: m.m }))} index={mC} onIndex={(i) => { setMi(i); setWi(0); }} />
              <WheelCol items={weeks.map((w) => tm("library.wheelWeek", { w: w.w, m: months[mC]?.m ?? 0, start: w.start, end: w.end }))} index={wC} onIndex={(i) => setWi(i)} />
            </div>
            <button onClick={go} className="btn btn--primary btn--lg" style={{ marginTop: 16, width: "100%" }}>{tm("library.go")}</button>
          </div>
        </>
      )}
    </div>
  );
}


/* ---------- Upload flow (real: extract meta → POST → PUT file → thumbnail) ---------- */
// 음악 파일(MP3 등) — 서버가 업로드 마무리 때 "음악 + 정지 화면" MP4 로 바꾼다(lib/audio-upload.ts).
// 그래서 여기선 길이만 읽고, 화면 줄이기·썸네일 캡처는 건너뛴다(썸네일도 서버가 표지로 채운다).
const AUDIO_EXT = /\.(mp3|m4a|aac|wav|ogg|oga|flac)$/i;
const isAudioFile = (f: File) => f.type.startsWith("audio/") || AUDIO_EXT.test(f.name);

function audioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("audio");
    a.preload = "metadata";
    const done = (sec: number) => { URL.revokeObjectURL(url); resolve(sec); };
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) ? Math.round(a.duration) : 0);
    a.onerror = () => done(0); // 브라우저가 못 읽는 형식이어도 업로드는 된다(길이는 서버가 다시 잰다)
    a.src = url;
  });
}

function extractMeta(file: File): Promise<{ durationSec: number; thumb: Blob | null }> {
  if (isAudioFile(file)) return audioDuration(file).then((durationSec) => ({ durationSec, thumb: null }));
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.src = url;
    let durationSec = 0;
    const fail = () => { URL.revokeObjectURL(url); resolve({ durationSec, thumb: null }); };
    v.onloadedmetadata = () => {
      durationSec = Math.round(v.duration) || 0;
      // seek to ~1s for a thumbnail frame
      const t = Math.min(1, (v.duration || 2) / 2);
      v.currentTime = t;
    };
    v.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const w = v.videoWidth || 640;
        const h = v.videoHeight || 360;
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return fail();
        ctx.drawImage(v, 0, 0, w, h);
        canvas.toBlob((blob) => { URL.revokeObjectURL(url); resolve({ durationSec, thumb: blob }); }, "image/jpeg", 0.82);
      } catch {
        fail();
      }
    };
    v.onerror = fail;
  });
}

type UpStat = { mbps: number; sentMB: number; totalMB: number; sec: number };

// tus 업로드 — 서버가 표준 Upload-Offset 으로 재개 지점을 관리하고, 끊긴 조각만 재시도한다.
const TUS_CHUNK_SIZE = 8 * 1048576;
function putChunked(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
  onStat?: (s: UpStat) => void,
): Promise<void> {
  const t0 = performance.now();
  const report = (sent: number, total: number) => {
    onProgress(total > 0 ? Math.round((sent / total) * 100) : 0);
    const sec = (performance.now() - t0) / 1000;
    onStat?.({
      mbps: sec > 0 ? sent / 1048576 / sec : 0, // 누적 평균 처리량(캡/회선 비교용)
      sentMB: sent / 1048576,
      totalMB: total / 1048576,
      sec,
    });
  };

  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: url,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: [0, 1000, 3000, 5000],
      removeFingerprintOnSuccess: true,
      metadata: { filename: file.name, filetype: file.type || "video/mp4" },
      onProgress: report,
      onSuccess: () => resolve(),
      onError: reject,
    });
    upload.start();
  });
}

export type ActiveUpload = { title: string; pct: number; phase: "compress" | "upload" };
export function UploadScreen({ dojangId, onClose, onToast, onUploaded, onActive, returnLabel }: {
  dojangId: string;
  onClose: () => void;
  onToast: (m: string) => void;
  onUploaded: (newVideoId?: string) => void | Promise<void>; // 완료된 영상 id — 재생목록에서 업로드한 경우 자동 추가에 사용
  onActive: (u: ActiveUpload | null) => void; // 진행 상황을 페이지로 끌어올려 라이브러리에 표시
  returnLabel?: string; // 완료 화면 버튼 문구 — 재생목록에서 진입했으면 '재생목록으로' (기본: 라이브러리로)
}) {
  const tm = useTranslations("control.media");
  const [stage, setStage] = useState<"pick" | "extract" | "meta" | "compress" | "upload" | "done">("pick");
  const [title, setTitle] = useState("");
  const [prog, setProg] = useState(0);
  const [stat, setStat] = useState<UpStat | null>(null); // 업로드 처리량 계측(측정용)
  const [shrunk, setShrunk] = useState(false); // 다운스케일이 실제로 수행됐는지(진행률 합산 가중용)
  const startRef = useRef(0); // 업로드 시작 시각(실패 시 소요시간 텔레메트리용)
  const [err, setErr] = useState("");
  const [thumbPreview, setThumbPreview] = useState<string | null>(null);
  const [ytUrl, setYtUrl] = useState(""); // 유튜브 URL 붙여넣기 입력
  const [ytBusy, setYtBusy] = useState(false);
  const [addedKind, setAddedKind] = useState<"file" | "youtube">("file"); // 완료 화면 문구 분기
  const fileRef = useRef<File | null>(null);
  const metaRef = useRef<{ durationSec: number; thumb: Blob | null }>({ durationSec: 0, thumb: null });
  const uploadedIdRef = useRef<string | undefined>(undefined); // 방금 업로드한 영상 id (재생목록 자동 추가용)
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => { if (thumbPreview) URL.revokeObjectURL(thumbPreview); }, [thumbPreview]);

  // onUploaded(자동 추가·목록 새로고침) 완료를 기다린 뒤 닫아야, 재생목록 복귀 시 새 영상이 바로 보인다
  const finish = async () => { onToast(tm("upload.addedToast", { title })); await onUploaded(uploadedIdRef.current); onClose(); };

  // 업로드 완료 화면은 3초 뒤 자동으로 라이브러리로 이동 (버튼 직접 누르면 언마운트되며 타이머 정리)
  useEffect(() => {
    if (stage !== "done") return;
    const t = setTimeout(finish, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const onPick = async (file: File) => {
    fileRef.current = file;
    setTitle(file.name.replace(/\.[^.]+$/, ""));
    setStage("extract");
    metaRef.current = await extractMeta(file);
    setThumbPreview(metaRef.current.thumb ? URL.createObjectURL(metaRef.current.thumb) : null);
    setStage("meta");
  };

  const doUpload = async () => {
    const file = fileRef.current;
    if (!file) return;
    const upTitle = title.trim() || file.name;
    setErr("");
    setStat(null);
    setShrunk(false);
    startRef.current = performance.now();
    try {
      // 1) 업로드 전 다운스케일(1080p 초과만) — 보낼 바이트를 줄여 전송시간 단축.
      //    미지원/이미 작음/변환불가는 원본 그대로 반환되어 업로드는 항상 진행됨.
      let uploadFile = file;
      let didShrink = false;
      if (!isAudioFile(file) && canDownscale()) {
        setStage("compress");
        setProg(0);
        const r = await maybeDownscale(file, (p) => {
          setProg(p);
          onActive({ title: upTitle, pct: Math.round(p * 0.8), phase: "compress" });
        });
        uploadFile = r.file;
        didShrink = r.shrunk;
        setShrunk(r.shrunk);
      }

      // 2) 메타 생성(줄인 크기 기준) → 병렬 청크 업로드
      setStage("upload");
      setProg(0);
      setStat(null);
      const { durationSec, thumb } = metaRef.current;
      const meta = await fetch(`/api/dojangs/${dojangId}/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim() || file.name, durationSec, sizeBytes: uploadFile.size }),
      }).then((r) => r.json());
      if (!meta?.video?.id) throw new Error(meta?.error?.message ?? "메타 생성 실패");
      uploadedIdRef.current = meta.video.id; // 완료 시 재생목록 자동 추가용
      const upT0 = performance.now(); // 전송(PUT) 구간만 측정 — 업로드 지연 진단
      await putChunked(meta.uploadUrl, uploadFile, (p) => {
        setProg(p);
        onActive({ title: upTitle, pct: Math.min(didShrink ? 80 + Math.round(p * 0.2) : p, 99), phase: "upload" });
      }, setStat);
      perf("upload_put", performance.now() - upT0, { videoId: meta.video.id, bytes: uploadFile.size, shrunk: didShrink });
      if (thumb) {
        await fetch(`/api/videos/${meta.video.id}/thumbnail`, { method: "POST", body: thumb }).catch(() => {});
      }
      setStage("done");
      onUploaded();    // 실제 영상이 목록에 뜨도록 새로고침(화면 떠난 뒤에도 실행됨)
      onActive(null);  // 합성 '전송 중' 카드 제거
    } catch (e) {
      console.warn("upload failed:", (e as Error).message); // 기술 정보는 콘솔에만
      perf("upload_put", (performance.now() - startRef.current), { bytes: fileRef.current?.size }, false, (e as Error)?.message?.slice(0, 64));
      // 422 = 서버가 파일을 거절(깨진 파일·2시간 넘는 음악 — lib/audio-upload). 인터넷 탓이 아니니 다른 안내를 띄우고,
      // 못 쓰는 칸이 라이브러리에 남지 않게 방금 만든 항목을 지운다.
      const rejected = (e as { originalResponse?: { getStatus?: () => number } })?.originalResponse?.getStatus?.() === 422;
      if (rejected && uploadedIdRef.current) {
        void fetch(`/api/videos/${uploadedIdRef.current}`, { method: "DELETE" }).catch(() => {});
        uploadedIdRef.current = undefined;
      }
      setErr(tm(rejected ? "upload.unsupportedFile" : "upload.uploadFailed"));
      setStage("meta");
      onActive(null);
    }
  };

  // 유튜브 URL 추가 — 파일 업로드 없이 즉시 영상 생성(썸네일은 유튜브 CDN 규격본 자동).
  const doYoutube = async () => {
    const url = ytUrl.trim();
    const t = title.trim();
    if (!url || !t || ytBusy) return;
    setErr("");
    setYtBusy(true);
    try {
      const res = await fetch(`/api/dojangs/${dojangId}/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: t, youtubeUrl: url }),
      });
      const meta = await res.json();
      if (!res.ok || !meta?.video?.id) throw new Error(meta?.error?.message ?? "추가 실패");
      uploadedIdRef.current = meta.video.id;
      setAddedKind("youtube");
      setStage("done");
      onUploaded(); // 목록 새로고침
    } catch (e) {
      const m = (e as Error).message ?? "";
      setErr(m.includes("유튜브") ? m : tm("upload.ytFailed"));
    } finally {
      setYtBusy(false);
    }
  };

  // "최적화 → 전송"을 진행률 하나로 이어붙임: 변환이 있었으면 0–80%(최적화)+80–100%(전송),
  // 없었으면 전송이 0–100% 전체를 차지. 사용자에겐 끊김 없는 단일 '업로드' 흐름으로 보임.
  const rawPct =
    stage === "compress"
      ? Math.round(prog * 0.8)
      : shrunk ? 80 + Math.round(prog * 0.2) : prog;
  // 바이트는 다 보냈지만(prog 100) 서버 수신 확정·썸네일 등 마무리가 남은 구간.
  // 업로드 바가 "100%인데 계속 진행 중"으로 보이는 걸 막기 위해 99%로 잡아두고 "마무리 중"으로 표기.
  const finalizing = stage === "upload" && prog >= 100;
  const uploadPct = Math.min(rawPct, 99);

  return (
    <div className="screen col" style={{ height: "100%" }}>
      {/* 다른 화면(설정·라이브러리)과 동일한 스크롤 반응 플로팅 헤더 — 헤더가 스크롤 컨테이너 '안'의 sticky 여야
          ScrollSolidify 가 --hd-solid 를 먹여 위=반투명·내리면 불투명이 된다. */}
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
        <div className="ctl-hd-sticky"><AppBar title={tm("upload.title")} onBack={onClose} /></div>
        <div className="col" style={{ padding: "8px 22px 22px", flex: 1 }}>
        <input ref={inputRef} type="file" accept="video/mp4,video/*,audio/*,.mp3,.m4a" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} />

        {stage === "pick" && (
          <div className="col gap-12" style={{ flex: 1 }}>
            <button onClick={() => inputRef.current?.click()} className="col gap-10" style={{
              border: "1.5px dashed var(--border-strong)", borderRadius: "var(--r-lg)", background: "var(--glass-bg)",
              backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
              boxShadow: "var(--glass-shadow), inset 0 1px 0 var(--glass-hi)",
              padding: "36px 20px", cursor: "pointer", alignItems: "center", marginTop: 6,
            }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--accent-soft)", display: "grid", placeItems: "center" }}>
                <I.upload size={30} style={{ color: "var(--accent)" }} />
              </div>
              <div className="col gap-3" style={{ alignItems: "center" }}>
                <div style={{ fontWeight: 750, fontSize: 16.5 }}>{tm("upload.chooseFile")}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--accent)", textAlign: "center" }}>{tm("upload.tapToChoose")}</div>
                <div className="muted" style={{ fontSize: 12, textAlign: "center" }}>{tm("upload.mp4Supported")}</div>
              </div>
            </button>
            <div className="col gap-9" style={{ background: "var(--glass-bg)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)", border: "1px solid var(--glass-brd)", borderRadius: "var(--r-lg)", padding: "13px 14px", boxShadow: "inset 0 1px 0 var(--glass-hi)" }}>
              <div className="sec-title" style={{ fontSize: 13, fontWeight: 800, color: "var(--text-1)", letterSpacing: "-.01em" }}>{tm("upload.howTitle")}</div>
              {[["1", tm("upload.step1")], ["2", tm("upload.step2")], ["3", tm("upload.step3")]].map(([n, s]) => (
                <div key={n} className="row gap-8" style={{ alignItems: "center", fontSize: 13.5 }}>
                  <span style={{ width: 21, height: 21, borderRadius: "50%", background: "var(--accent-soft)", color: "var(--accent)", fontSize: 12, fontWeight: 800, display: "grid", placeItems: "center", flex: "none" }}>{n}</span>
                  <span style={{ color: "var(--text-2)" }}>{s}</span>
                </div>
              ))}
            </div>

            <div className="row gap-10" style={{ alignItems: "center", margin: "-3px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--border-strong)" }} />
              <span className="muted" style={{ fontSize: 12 }}>{tm("common.or")}</span>
              <div style={{ flex: 1, height: 1, background: "var(--border-strong)" }} />
            </div>

            {/* 유튜브 URL 붙여넣기 — 파일 업로드 없이 임베드 영상 추가 */}
            <div className="col gap-10" style={{ background: "var(--glass-bg)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)", border: "1px solid var(--glass-brd)", borderRadius: "var(--r-lg)", padding: 14, boxShadow: "var(--glass-shadow), inset 0 1px 0 var(--glass-hi)" }}>
              <div className="row gap-8" style={{ alignItems: "center" }}>
                <I.video size={17} style={{ color: "var(--accent)" }} />
                <span style={{ fontWeight: 700, fontSize: 15 }}>{tm("upload.pasteYt")}</span>
              </div>
              <div className="field">
                <input className="input" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)}
                  placeholder={tm("upload.ytUrlPh")} inputMode="url" autoComplete="off"
                  onKeyDown={(e) => { if (e.key === "Enter") doYoutube(); }} />
              </div>
              <div className="field">
                <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder={tm("upload.videoTitlePh")}
                  onKeyDown={(e) => { if (e.key === "Enter") doYoutube(); }} />
              </div>
              {err && <div className="chip chip--off" style={{ color: "var(--danger)", height: "auto", padding: "8px 12px", whiteSpace: "normal" }}>{err}</div>}
              {/* 비활성(입력 전)에도 '버튼'임이 분명하게 — 옅은 판이 아니라 accent 테두리 아웃라인으로 존재감 유지 */}
              <button className="btn btn--primary btn--lg" onClick={doYoutube}
                disabled={!ytUrl.trim() || !title.trim() || ytBusy}
                style={(ytUrl.trim() && title.trim() && !ytBusy)
                  ? { marginTop: 2 }
                  : { marginTop: 2, opacity: 1, background: "var(--accent-soft)", color: "var(--accent)", border: "1.5px solid var(--accent)", fontWeight: 700 }}>
                {ytBusy ? tm("upload.adding") : tm("upload.addYt")}
              </button>
              <div className="row gap-6" style={{ fontSize: 13, color: "var(--text-1)", lineHeight: 1.6, alignItems: "flex-start", wordBreak: "keep-all" }}>
                <I.wifi size={14} style={{ flex: "none", marginTop: 3, color: "var(--accent)" }} />
                <span>{tm("upload.ytNote1")}<br />{tm("upload.ytNote2")}</span>
              </div>
            </div>
          </div>
        )}

        {stage === "extract" && (
          <div className="col gap-16" style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <div className="spin" style={{ color: "var(--accent)" }}><I.spinner size={42} sw={2} /></div>
            <div className="col gap-4" style={{ alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{tm("upload.checking")}</div>
            </div>
          </div>
        )}

        {(stage === "meta" || stage === "compress" || stage === "upload") && (
          <div className="col gap-16" style={{ marginTop: 8 }}>
            <div className="row gap-14">
              <div style={{ width: 108, height: 72, borderRadius: "var(--r-md)", background: "var(--surface-2)", display: "grid", placeItems: "center", flex: "none", overflow: "hidden" }}>
                {thumbPreview
                  ? <img src={thumbPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : fileRef.current && isAudioFile(fileRef.current)
                    ? <I.music size={26} style={{ color: "var(--accent)" }} />
                    : <I.film size={24} className="dim" />}
              </div>
              <div className="col gap-6" style={{ flex: 1 }}>
                <div className="row gap-8"><span className="chip chip--live" style={{ height: 22 }}><I.check size={12} sw={2.4} />{tm("upload.checked")}</span></div>
                <div className="muted mono" style={{ fontSize: 12.5 }}>{tm("upload.length")} {fmt(metaRef.current.durationSec)} · {fileRef.current ? (fileRef.current.size / 1024 / 1024).toFixed(0) : "?"}MB</div>
              </div>
            </div>
            <div className="field">
              <label>{tm("upload.titleLabel")}</label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tm("upload.videoTitlePh")} disabled={stage !== "meta"} />
            </div>
            {err && <div className="chip chip--off" style={{ color: "var(--danger)", height: "auto", padding: "8px 12px", whiteSpace: "normal" }}>{err}</div>}
            {stage === "meta" ? (
              <button className="btn btn--primary btn--lg" onClick={doUpload} disabled={!title.trim()} style={{ opacity: title.trim() ? 1 : 0.4 }}>{tm("upload.startUpload")}</button>
            ) : (
              <div className="col gap-8">
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <span className="muted" style={{ fontSize: 13 }}>{finalizing ? tm("upload.finishing") : tm("upload.uploading")} <span className="mono">{uploadPct}%</span></span>
                </div>
                <Track value={uploadPct} h={8} />
              </div>
            )}
          </div>
        )}

        {stage === "done" && (
          <div className="col gap-18" style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--live-soft)", display: "grid", placeItems: "center" }}>
              <I.check size={38} sw={2.4} style={{ color: "var(--live)" }} />
            </div>
            <div className="col gap-4" style={{ alignItems: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 19 }}>{addedKind === "youtube" ? tm("upload.ytAdded") : tm("upload.done")}</div>
              <div className="muted" style={{ fontSize: 14 }}>{title}</div>
            </div>
            <button className="btn btn--primary btn--lg" style={{ width: "auto", padding: "0 40px" }}
              onClick={finish}>{returnLabel ?? tm("upload.backToLibrary")}</button>
          </div>
        )}
        </div>
      </ScrollSolidify>
    </div>
  );
}

/* ---------- Playlists ---------- */
// 기본 제공(시드) 재생목록 분류 — 시드 명명 규칙(lib/seed-reference.ts) 기준
const isBasic = (pl: PlaylistView) => pl.isDefault && pl.name.startsWith("기본동작");
const isTaegeuk = (pl: PlaylistView) => pl.isDefault && pl.name.startsWith("태극");
const taegeukNo = (s: string) => { const m = s.match(/(\d+)\s*장/); return m ? parseInt(m[1], 10) : 999; }; // '태극 3장'·'3장' → 3

/* 길게 누르기 — 모바일에서 카드 롱프레스로 보조 동작(삭제 등)을 띄운다. 탭(열기)·스크롤과 충돌 방지:
   10px 이상 움직이면 취소(스크롤로 간주), 발동 후 뒤따르는 click(열기)은 1회 무시. */
function useLongPress(onLong: () => void, enabled = true, ms = 500) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } start.current = null; };
  const handlers = enabled ? {
    onPointerDown: (e: React.PointerEvent) => {
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = setTimeout(() => {
        fired.current = true;
        if (typeof navigator !== "undefined" && "vibrate" in navigator) navigator.vibrate?.(30); // 햅틱 피드백
        onLong();
      }, ms);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current && (Math.abs(e.clientX - start.current.x) > 10 || Math.abs(e.clientY - start.current.y) > 10)) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(), // 롱프레스 시 네이티브 컨텍스트 메뉴 억제
  } : {};
  // 롱프레스가 발동했으면 뒤따르는 click(열기)을 1회 무시
  const consumeFired = () => { const f = fired.current; fired.current = false; return f; };
  return { handlers, consumeFired };
}

function PlaylistCard({ pl, i, onOpen, onDelete }: { pl: PlaylistView; i: number; onOpen: (pl: PlaylistView) => void; onDelete?: (pl: PlaylistView) => void }) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const { byId } = useData();
  const cover = byId[pl.items[0]]; // 단일 커버(첫 영상) — IdleBoard 행과 통일
  // 기본·품새 재생목록은 삭제 불가 → 롱프레스 비활성. 내 재생목록만 롱프레스로 삭제 확인.
  const lp = useLongPress(() => onDelete?.(pl), !!onDelete && !pl.isDefault);
  return (
    <button {...lp.handlers} onClick={() => { if (lp.consumeFired()) return; onOpen(pl); }} style={{
      display: "flex", alignItems: "center", gap: 15, width: "100%", textAlign: "left",
      padding: "15px 16px", background: "transparent", border: "none", cursor: "pointer", animationDelay: `${i * 40}ms`,
      WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none",
    }}>
      <MediaSlot>
        {cover ? <Poster video={cover} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />}
      </MediaSlot>
      <div className="col" style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: "-0.015em", color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdPlaylist(pl.name, locale)}</div>
        <div className="row" style={{ gap: 7, alignItems: "center", fontSize: 12.5, color: "var(--text-2)" }}>
          <span>{tm("count.videos", { count: pl.items.length })}</span>
          <span className="ctl-sub__sep" />
          <span className="row gap-3 tnum" style={{ alignItems: "center" }}><I.clock size={12} /> {fmt(playlistDuration(pl, byId))}</span>
        </div>
      </div>
      <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />
    </button>
  );
}


// 모션 감소 선호 감지 — 접기 애니메이션을 끌지 결정
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(m.matches);
    const on = () => setReduce(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return reduce;
}

// 부드러운 접기/펴기 — grid-template-rows 0fr↔1fr 트랜지션(내용 높이 auto 자동 애니메이션, 측정 불필요).
// ⚠️완전히 열린 뒤엔 overflow 를 visible 로 풀어 내부 position:sticky(GroupedLibrary 날짜헤더 등)가 안 깨지게 한다.
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const reduce = usePrefersReducedMotion();
  const [clipOff, setClipOff] = useState(open); // true=overflow visible(완전히 열림)
  useEffect(() => {
    if (!open) setClipOff(false);            // 닫기 시작 → 즉시 clip(스르륵 접힘)
    else if (reduce) setClipOff(true);       // 모션 감소 → 즉시 열림
    // 모션 있음+열기: 트랜지션 끝(onTransitionEnd)에서 clipOff=true
  }, [open, reduce]);
  return (
    <div
      // gridTemplateColumns: minmax(0,1fr) 필수 — 없으면 암시적 컬럼이 auto(=max-content)로 커져
      // 카드 제목(white-space:nowrap)의 전체 폭만큼 트랙이 부풀어 카드가 화면 밖으로 삐져나간다(오른쪽 잘림).
      style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gridTemplateRows: open ? "1fr" : "0fr", transition: reduce ? "none" : "grid-template-rows .24s ease" }}
      onTransitionEnd={(e) => { if (e.propertyName === "grid-template-rows" && open) setClipOff(true); }}
    >
      <div style={{ overflow: clipOff ? "visible" : "hidden", minWidth: 0, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// ===== 라이브러리 — 영상 + 재생목록 병합(Phase 2) =====
// 앱 전역 유일 세그먼트 [기본·품새 | 내 자료]. '영상 vs 재생목록' 축은 세그먼트가 아니라
// '내 자료' 안의 스크롤 섹션 2개(재생목록 → 영상)로 해체 → 이중 세그먼트를 원천 제거.
// 기본·품새 = 묶음(기본동작/태극) + 품새 카드(→ FormDrill). 업로드/새 재생목록은 편집 권한이 있는 '내 자료'에만.
export function LibraryScreen({
  onOpen, onOpenForm, onCreate, onDelete, onUpload, onMenu, onPlay, uploading, masterForms, backRef,
  seg, onSeg, group, onGroup,
}: {
  onOpen: (pl: PlaylistView) => void;
  onOpenForm?: (form: MasterForm) => void;
  onCreate: () => void;
  onDelete?: (pl: PlaylistView) => void;
  onUpload: () => void;
  onMenu: (v: VideoView) => void;
  onPlay: (ids: string[], index: number, title: string) => void;
  uploading?: ActiveUpload | null;
  masterForms?: MasterForm[];
  backRef?: { current: (() => boolean) | null }; // 하드웨어 뒤로가기가 내부 뎁스(묶음/검색)를 먼저 pop하도록 back 핸들러 등록
  // seg/group 은 부모(page.tsx)가 보유 — 품새 카드 → 전체화면 드릴(formdrill)로 나갔다 뒤로 오면
  // LibraryScreen 이 새로 마운트되므로, 내부 useState 로 두면 태극 묶음 진입이 사라진다. 부모 상태로 유지해 복귀.
  seg: "default" | "mine";
  onSeg: (v: "default" | "mine") => void;
  group: null | "basic" | "taegeuk"; // 기본·품새 안 '기본동작'·'태극' 묶음 진입
  onGroup: (g: null | "basic" | "taegeuk") => void;
}) {
  const tm = useTranslations("control.media");
  const segRef = useSegRefLabel();
  const locale = useLocale();
  const { videos, playlists, byId } = useData();
  const [plOpen, setPlOpen] = useState(true);   // '내 재생목록' 섹션 접기/펴기(쉐브론)
  const [vidOpen, setVidOpen] = useState(true); // '내 영상' 섹션 접기/펴기(쉐브론)
  const [srchPlOpen, setSrchPlOpen] = useState(true);   // 검색결과 '재생목록' 그룹 접기/펴기
  const [srchVidOpen, setSrchVidOpen] = useState(true); // 검색결과 '영상' 그룹 접기/펴기
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  // 하드웨어 뒤로가기 소비자 등록 — 묶음 진입이면 묶음 해제, 검색 열림이면 검색 닫기, 그 외 false(부모가 탭 폴백).
  useEffect(() => {
    if (!backRef) return;
    backRef.current = () => {
      if (group) { onGroup(null); return true; }
      if (searchOpen || q) { setQ(""); setSearchOpen(false); return true; }
      return false;
    };
    return () => { backRef.current = null; };
  }, [backRef, group, onGroup, searchOpen, q]);

  const basics = showsBasics(locale) ? playlists.filter(isBasic) : []; // 영문 UI = 기본동작 숨김
  const taegeuks = playlists.filter(isTaegeuk).sort((a, b) => taegeukNo(a.name) - taegeukNo(b.name));
  const poomsae = playlists.filter((pl) => pl.isDefault && !isBasic(pl) && !isTaegeuk(pl));
  const minePls = playlists.filter((pl) => !pl.isDefault);
  const mineVideos = videos.filter((v) => !isReferenceVideo(v));

  // 재생목록 카드 탭 — 마스터 표준 품새면 전체화면 드릴(FormDrill), 아니면(기본동작·내 것) 기존 상세로.
  const openPl = (pl: PlaylistView) => {
    const f = onOpenForm ? masterForms?.find((m) => m.playlistId === pl.id || m.name === pl.name) : undefined;
    if (f) onOpenForm!(f); else onOpen(pl);
  };
  const switchSeg = (v: "default" | "mine") => { onSeg(v); onGroup(null); };

  const searchBtn = (
    <button className="iconbtn iconbtn--plain" aria-label={searchOpen || query ? tm("library.closeSearch") : tm("library.search")}
      onClick={() => { if (searchOpen || query) { setQ(""); setSearchOpen(false); } else setSearchOpen(true); }}>
      {searchOpen || query ? <I.x size={21} sw={2.2} /> : <I.search size={21} />}
    </button>
  );

  // 묶음 카드(기본동작 지역별 / 태극 장별) — 탭 시 그 묶음 목록으로 진입. GlassRow 로 통일.
  const groupCard = (kind: "basic" | "taegeuk", list: PlaylistView[], label: string, sub: string) => (
    <GlassRow key={kind}
      thumb={byId[list[0]?.items[0]] ? <Poster video={byId[list[0].items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />}
      title={label}
      sub={<span style={{ fontSize: 12.5, color: "var(--text-2)" }}>{sub}</span>}
      nav onClick={() => onGroup(kind)} />
  );

  // 섹션 헤더('내 재생목록'·'내 영상') — 쉐브론(▾)으로 접기/펴기 + 우측 인라인 CTA(새 재생목록·업로드). CTA는 '내 자료'에만.
  const secHead = (title: string, count: number, open: boolean, onToggle: () => void, cta: { label: string; icon: ReactNode; onClick: () => void }) => (
    <div className="row" style={{ alignItems: "center", justifyContent: "space-between", padding: "6px 2px 2px", gap: 8 }}>
      <button onClick={onToggle} aria-expanded={open} className="row gap-6" style={{ background: "none", border: "none", cursor: "pointer", alignItems: "center", padding: 0, flex: 1, minWidth: 0, textAlign: "left" }}>
        <I.chevD size={16} className="dim" style={{ flex: "none", transform: open ? "none" : "rotate(-90deg)", transition: "transform .18s ease" }} />
        <span style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: "-0.01em", color: "var(--text-1)" }}>{title}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)" }}>{count}</span>
      </button>
      <button className="row gap-4" onClick={cta.onClick} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontWeight: 700, fontSize: 13, alignItems: "center", flex: "none" }}>{cta.icon}{cta.label}</button>
    </div>
  );

  const emptyBox = (icon: ReactNode, text: string, cta?: ReactNode) => (
    <div className="col gap-10" style={{ alignItems: "center", padding: "28px 20px", color: "var(--text-3)" }}>
      {icon}<span style={{ fontSize: 14 }}>{text}</span>{cta}
    </div>
  );

  // 묶음(기본동작/태극) 내부 — 그 묶음의 재생목록 카드만. 뒤로가기로 최상위 복귀.
  if (group) {
    const list = group === "basic" ? basics : taegeuks;
    return (
      <div className="screen col" style={{ height: "100%" }}>
        <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
          <div className="ctl-hd-sticky"><AppBar title={group === "basic" ? tm("library.basicGroup") : tm("library.taegeukGroup")} onBack={() => onGroup(null)} sub={tm("library.groupCount", { count: list.length })} /></div>
          <div className="col" style={{ padding: "4px 18px 18px" }}>
            <GlassGroup glass inset={97}>
              {list.map((pl, i) => <PlaylistCard key={pl.id} pl={pl} i={i} onOpen={openPl} />)}
            </GlassGroup>
          </div>
        </ScrollSolidify>
      </div>
    );
  }

  const defaultCount = basics.length + taegeuks.length + poomsae.length;
  const showUploading = !!uploading && seg === "mine";
  // 통합(글로벌) 검색 — 세그먼트 무시하고 전체 재생목록 + 전체 영상(품새 레퍼런스 포함) 제목 매칭.
  const plHits = query
    ? playlists.filter((pl) => (showsBasics(locale) || !isBasic(pl)) && `${pl.name} ${tkdPlaylist(pl.name, locale)}`.toLowerCase().includes(query))
    : [];
  const vidHits = query ? videos.filter((v) => tkdSearchText(v.title, locale).includes(query)) : [];
  // 검색결과 그룹 헤더 — 쉐브론으로 접기/펴기(재생목록/영상 각각)
  const resHead = (t: string, n: number, open: boolean, onToggle: () => void) => (
    <button onClick={onToggle} aria-expanded={open} className="row gap-6" style={{
      background: "none", border: "none", cursor: "pointer", alignItems: "center", padding: "8px 2px 2px", width: "100%", textAlign: "left",
    }}>
      <I.chevD size={16} className="dim" style={{ flex: "none", transform: open ? "none" : "rotate(-90deg)", transition: "transform .18s ease" }} />
      <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: ".02em", color: "var(--text-2)" }}>{t}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)" }}>{n}</span>
    </button>
  );

  return (
    <div className="screen col" style={{ height: "100%" }}>
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
      <div className="ctl-hd-sticky"><AppBar title={tm("library.title")} right={searchBtn} /></div>
      {!query && (
        <div style={{ padding: "0 18px 10px" }}>
          <Segmented value={seg} onChange={switchSeg} options={[
            { value: "default", label: segRef },
            { value: "mine", label: tm("library.myContent") },
          ]} />
        </div>
      )}
      {(searchOpen || query) && (
        <div style={{ padding: `${query ? 12 : 0}px 18px 10px` }}>{/* 검색 중엔 세그가 숨어 상단바에 딱 붙으므로 위 여백 */}
          <div className="vsearch">
            <I.search size={17} className="dim" />
            <input autoFocus className="vsearch__input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tm("library.searchAll")} inputMode="search" />
            {q && <button className="vsearch__clear" onClick={() => setQ("")} aria-label={tm("common.clearSearch")}><I.x size={14} sw={2.2} /></button>}
          </div>
        </div>
      )}

      {query ? (
        /* ===== 통합 검색 결과 — 세그먼트 무시, 재생목록/영상 타입별 그룹 ===== */
        <div key="search" className="slide-in col gap-10" style={{ padding: "4px 18px 18px" }}>
          {plHits.length === 0 && vidHits.length === 0 && emptyBox(<I.search size={32} />, tm("common.noResults", { q: q.trim() }))}
          {plHits.length > 0 && (<>
            {resHead(tm("library.playlists"), plHits.length, srchPlOpen, () => setSrchPlOpen((o) => !o))}
            <Collapse open={srchPlOpen}>
              <GlassGroup glass inset={97}>
                {plHits.map((pl, i) => <PlaylistCard key={pl.id} pl={pl} i={i} onOpen={openPl} onDelete={onDelete} />)}
              </GlassGroup>
            </Collapse>
          </>)}
          {vidHits.length > 0 && (<>
            {resHead(tm("library.videos"), vidHits.length, srchVidOpen, () => setSrchVidOpen((o) => !o))}
            <Collapse open={srchVidOpen}>
              <GlassGroup glass inset={97}>
                {vidHits.map((v, i) => <VideoRow key={v.id} v={v} delay={i * 30}
                  onMenu={isReferenceVideo(v) ? undefined : onMenu}
                  onClick={() => onPlay(vidHits.map((x) => x.id), i, tm("library.searchResults"))} />)}
              </GlassGroup>
            </Collapse>
          </>)}
        </div>
      ) : seg === "default" ? (
        <div key="default" className="slide-in col" style={{ padding: "4px 18px 18px" }}>
          {defaultCount > 0 ? (
            <GlassGroup glass inset={97}>
              {basics.length > 0 && groupCard("basic", basics, tm("library.basicGroup"), tm("library.basicGroupSubN", { count: basics.length }))}
              {taegeuks.length > 0 && groupCard("taegeuk", taegeuks, tm("library.taegeukGroup"), tm("library.taegeukGroupSubN", { count: taegeuks.length }))}
              {poomsae.map((pl, i) => <PlaylistCard key={pl.id} pl={pl} i={i} onOpen={openPl} />)}
            </GlassGroup>
          ) : emptyBox(<I.layers size={34} />, tm("library.noDefault"))}
        </div>
      ) : (
        <div key="mine" className="slide-in col gap-10" style={{ padding: "4px 18px 18px" }}>
          {secHead(tm("library.myPlaylists"), minePls.length, plOpen, () => setPlOpen((o) => !o), { label: tm("library.newPlaylist"), icon: <I.plus size={16} />, onClick: onCreate })}
          <Collapse open={plOpen}>
            {minePls.length === 0
              ? emptyBox(<I.layers size={30} />, tm("library.noMyPlaylists"),
                  <button className="btn btn--primary" style={{ height: 44, padding: "0 20px", width: "auto" }} onClick={onCreate}><I.plus size={18} />{tm("library.newPlaylist")}</button>)
              : <GlassGroup glass inset={97}>{minePls.map((pl, i) => <PlaylistCard key={pl.id} pl={pl} i={i} onOpen={onOpen} onDelete={onDelete} />)}</GlassGroup>}
          </Collapse>

          <div style={{ height: 8 }} />
          {secHead(tm("library.myVideos"), mineVideos.length, vidOpen, () => setVidOpen((o) => !o), { label: tm("common.upload"), icon: <I.upload size={15} />, onClick: onUpload })}
          <Collapse open={vidOpen}>
            <div className="col gap-10">
              {showUploading && uploading && (
                <GlassGroup glass inset={97}>
                  <div className="row" style={{ gap: 12, padding: "12px 16px", alignItems: "center" }}>
                    <MediaSlot>
                      <span className="spin" style={{ color: "var(--accent)" }}><I.spinner size={20} sw={2.2} /></span>
                    </MediaSlot>
                    <div className="col gap-4" style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: "-.015em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{uploading.title}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{uploading.phase === "compress" ? tm("library.optimizing") : tm("library.uploadingShort")} · {uploading.pct}%</div>
                      <Track value={uploading.pct} h={4} />
                    </div>
                  </div>
                </GlassGroup>
              )}
              {mineVideos.length === 0 && !showUploading
                ? emptyBox(<I.film size={30} />, tm("library.noMyVideos"),
                    <button className="btn btn--primary" style={{ height: 44, padding: "0 20px", width: "auto" }} onClick={onUpload}><I.upload size={17} />{tm("library.uploadVideo")}</button>)
                : <GroupedLibrary videos={mineVideos} onPlay={onPlay} onMenu={onMenu} />}
            </div>
          </Collapse>
        </div>
      )}
      </ScrollSolidify>
    </div>
  );
}

// 품새 전체/마디별 드릴 — 전체화면(탭호스트 바깥, 네비바 없음). 내 재생목록 상세(PlaylistDetail)와 동일 위상.
export function FormDrillScreen({ form, onBack, onPlay, onPlaySet, onPlayMachiSet, onMenu, onToggleHidden, onAddDefault, onRemoveDefault, onReorderDefault }: {
  form: MasterForm;
  onBack: () => void;
  /** 상단 ⋯ — '이 품새 받아두기'(2026-08-28). 없으면 ⋯ 자체를 안 띄운다(불러오기 시트에선 안 넘어온다). */
  onMenu?: (offline: { items: SetItem[]; plan: SetPlan | null }) => void;
  onPlay: (v: MasterPlayVideo) => void;
  onPlaySet: (playlistId: string) => void;
  onPlayMachiSet?: (videos: MasterPlayVideo[], title: string, startIndex?: number, practice?: DrillPlayOpt | null) => void;
  // 도장별 커스터마이즈(품새 제공영상 눈알 숨김 + 내 영상 덧붙이기) — form.playlistId 대상.
  onToggleHidden?: (videoId: string, hidden: boolean) => void;
  onAddDefault?: (videoIds: string[]) => void; // 여러 개를 한 번에
  onRemoveDefault?: (videoId: string) => void;
  onReorderDefault?: (videoIds: string[]) => void;
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const { videos, byId, plById } = useData();
  const pl = form.playlistId ? plById[form.playlistId] : undefined;
  const editable = !!(form.playlistId && onToggleHidden); // 커스터마이즈 가능(불러오기 시트에선 콜백 없음)
  // 눈알 낙관적 오버레이 — 토글 즉시 반영, 목록 새로고침으로 확정.
  const [hiddenOv, setHiddenOv] = useState<Record<string, boolean>>(pl?.hidden ?? {});
  useEffect(() => { setHiddenOv(pl?.hidden ?? {}); }, [pl]);
  const [pick, setPick] = useState(false);
  const addedIds = pl ? Object.keys(pl.added) : [];
  // 덧붙일 수 있는 '내 영상' — 이미 이 재생목록에 있는 건 제외, 제공(참조) 영상 제외.
  const avail = videos.filter((v) => !isReferenceVideo(v) && !(pl?.items.includes(v.id)));

  // 미리 받아둘 것 — 이 화면에 뜨는 것 전부(전체·품마디·품·낱기술)에 내가 덧붙인 영상까지.
  // 🛑 재생목록 '태백'(3시점 3편)이 아니라 **품새 화면이 담고 있는 것**(18편)이 기준이다 —
  //    상단이 "영상 18개"라고 말하고 있고, ⋯ 는 그 화면 전체의 메뉴다.
  //    화질 판정은 제어화면 상세와 같은 것을 쓴다(다른 걸 받으면 헛일). PlaylistDetail 과 같은 규칙.
  const mq = useMirrorQuality();
  const offline: SetItem[] = [...form.videos, ...addedIds.map((id) => byId[id])]
    .filter((v): v is NonNullable<typeof v> => !!v && !hiddenOv[v.id] && !!(v.mirror || (mq.hiCache && v.mirrorHi)))
    .map((v) => (mq.hiCache && v.mirrorHi
      ? { rel: v.mirrorHi, tag: v.mirrorHiTag ?? null, fb: v.mirror ? { rel: v.mirror, tag: v.mirrorTag ?? null } : null }
      : { rel: v.mirror as string, tag: v.mirrorTag ?? null }));
  const offlinePlan = useSetPlan(offline);

  const custom = editable ? {
    order: pl?.items ?? [], // 서버가 이 도장 커스텀 순서로 내려준 통합 목록(제공+추가)
    hiddenIds: new Set(Object.entries(hiddenOv).filter(([, h]) => h).map(([id]) => id)),
    added: new Map(addedIds.map((id) => {
      const v = byId[id];
      return [id, { id, title: v?.title ?? "", durationSec: v?.durationSec ?? null, thumbnail: v?.thumbnail ?? null }] as const;
    })),
    onToggleHidden: (vid: string, h: boolean) => { setHiddenOv((s) => ({ ...s, [vid]: h })); onToggleHidden!(vid, h); },
    onRemoveAdded: (vid: string) => onRemoveDefault?.(vid),
    onReorder: (videoIds: string[]) => onReorderDefault?.(videoIds),
    onAddClick: () => setPick(true),
  } : undefined;

  return (
    <div className="screen col" style={{ height: "100%" }}>
      <AppBar title={tkdForm(form.name, locale)} onBack={onBack} sub={tm("count.videos", { count: form.videos.length })}
        right={onMenu && offline.length > 0 && offlinePlan
          ? <button className="iconbtn iconbtn--plain" onClick={() => onMenu({ items: offline, plan: offlinePlan })} aria-label={tm("common.more")}><I.dots size={20} /></button>
          : undefined} />
      <div className="col" style={{ flex: 1, minHeight: 0, padding: "4px 18px max(18px, env(safe-area-inset-bottom))" }}>
        {/* 받는 중에만 뜬다 — 버튼은 ⋯ 안에 있다(재생목록 화면과 같은 규칙). */}
        <SetPrefetchBar />
        <FormDrill form={form} onPlay={onPlay} onPlaySet={onPlaySet} onPlayMachiSet={onPlayMachiSet} onBack={onBack} hideHeader custom={custom} addedPlay={addedPlaySet(form.playlistId, plById, byId)} />
      </div>
      {pick && <AddVideoSheet avail={avail} mineOnly onAddMany={(ids) => onAddDefault?.(ids)} onClose={() => setPick(false)} />}
    </div>
  );
}

/* 기본·품새(레퍼런스) 영상을 2단계로 묶는다 — 영상은 자신이 속한 기본 제공 재생목록으로 분류.
   · 태극     → 상위 "태극", 하위 "1장"·"2장"… (태극 1~8장 재생목록을 한 그룹으로 합치되 장별로 나눔)
   · 기본동작 → 상위 "기본동작", 하위 지역명(서울태권도협회…)
   · 고려·금강·태백 → 상위 그룹 그대로, 하위 없음
   재생목록(useData) 순서를 따르므로 태극→유단자 품새→지역별 기본동작 순. */
type RefSub = { label: string | null; videos: VideoView[] };
type RefGroup = { label: string; subs: RefSub[]; count: number };

// 영상 추가 시트 · 품새 하위 묶음 내 정렬 키 — 전체(정면→후면→부감) 먼저, 그다음 품마디 번호 오름차순.
// 참조 메타(refSegment/refViewpoint/refSort)는 serializeVideo 가 동봉. 비참조(도장 업로드)는 뒤쪽·기존순 유지(안정정렬).
const REF_VP = ["정면", "후면", "부감"];
const isMachiV = (v: VideoView) => parseSegment(v.refSegment ?? null).kind === "machi";
// 구령 버전 손발손(refVariant)인가 — 제목이 하나둘과 같아서 목록에선 주황 꼬리표로 가른다.
const isSbsV = (v: VideoView) => !!(v.refVariant ?? "").trim();
function SbsTag() {
  const tm = useTranslations("control.media");
  return <span className="sbs-tag">{tm("detail.sbsTag")}</span>;
}
const machiNoV = (v: VideoView) => Number(parseSegment(v.refSegment ?? null).item) || 0;
// 품(조합동작)·낱기술(차기/서기/수기) — 이 시트에만 오는 '기본동작' 세부(DataCtx.breakdown). 드릴(FormDrill)과 같은 탭 구성.
const isPoomV = (v: VideoView) => segmentKindOf(v.refSegment ?? null) === "poom";
const isTechV = (v: VideoView) => segmentKindOf(v.refSegment ?? null) === "technique";
const techKindV = (v: VideoView) => parseSegment(v.refSegment ?? null).item; // "차기" | "서기" | "수기" | …
const TECH_KINDS = ["차기", "서기", "수기"]; // 노출·정렬 순서(드릴과 동일) — 그 밖의 종류는 뒤에 자연순
const bySortV = (a: VideoView, b: VideoView) => (a.refSort ?? 0) - (b.refSort ?? 0);
function refOrderKey(v: VideoView): number {
  const p = parseSegment(v.refSegment ?? null);
  const sort = v.refSort ?? 0;
  if (p.kind === "machi") return 100000 + (Number(p.item) || 0) * 100 + sort;
  if (p.kind === "poom") return 200000 + sort; // 품 → 낱기술 순으로 품마디 뒤에(검색 평면 목록에서의 순서)
  if (p.kind === "technique") return 300000 + sort;
  const vp = REF_VP.indexOf(v.refViewpoint ?? "");
  return (vp < 0 ? 90 : vp) * 100 + sort;
}

function groupReference(videos: VideoView[], playlists: PlaylistView[], byId: Record<string, VideoView>): RefGroup[] {
  const catOf = new Map<string, { top: string; sub: string | null }>(); // videoId → 분류
  // 품마디처럼 '전체'가 아니라 기본 재생목록에 안 담긴 마스터 영상은 소속 품새(refFormId)로 폴백 분류한다.
  // (기본 재생목록엔 전체 영상만 들어감 — syncMasterPlaylists. 그래서 품마디는 여기서 그 품새 밑으로 묶어야 '기타'로 안 샌다.)
  const catByForm = new Map<string, { top: string; sub: string | null }>(); // refFormId → 분류
  // 기본 재생목록 안 자리 — 정렬 키가 같을 때(기본동작 지역 영상은 refSort·시점이 없어 전부 동점) 라이브러리와 같은 순서로.
  // 없으면 videos(최신 업로드순) 그대로라 재생목록과 정반대(3품→…→1단)로 떴다.
  const posOf = new Map<string, number>();
  const subRank = new Map<string, number>(); // "top/sub" → 기본 재생목록 순번
  const topOrder: string[] = [];
  for (const p of playlists) {
    if (!p.isDefault) continue;
    let top = p.name, sub: string | null = null;
    if (p.name.startsWith("태극")) { top = "태극"; sub = p.name.replace(/^태극\s*/, "") || null; }
    else if (p.name.startsWith("기본동작")) { top = "기본동작"; sub = p.name.split("·").pop()?.trim() || null; }
    if (!topOrder.includes(top)) topOrder.push(top);
    if (!subRank.has(top + "/" + (sub ?? ""))) subRank.set(top + "/" + (sub ?? ""), subRank.size); // 하위(지역)도 라이브러리와 같은 재생목록 순서
    const cat = { top, sub };
    p.items.forEach((id, i) => { if (!posOf.has(id)) posOf.set(id, i); });
    for (const id of p.items) {
      if (!catOf.has(id)) catOf.set(id, cat);
      const fid = byId[id]?.refFormId; // 이 기본 재생목록이 담은 '전체' 영상의 소속 품새 id
      if (fid && !catByForm.has(fid)) catByForm.set(fid, cat);
    }
  }
  // top → { 하위 순서, 하위 라벨별 영상 }
  const tops = new Map<string, { subOrder: string[]; subs: Map<string, VideoView[]> }>();
  const etc: VideoView[] = [];
  for (const v of videos) {
    // 직접 멤버십(전체) → 없으면 소속 품새로 폴백(품마디는 기본 재생목록에 없어 여기서 그 품새 밑에 묶인다)
    const c = catOf.get(v.id) ?? (v.refFormId ? catByForm.get(v.refFormId) : undefined);
    if (!c) { etc.push(v); continue; }
    let t = tops.get(c.top);
    if (!t) { t = { subOrder: [], subs: new Map() }; tops.set(c.top, t); }
    const key = c.sub ?? "";
    if (!t.subs.has(key)) { t.subs.set(key, []); t.subOrder.push(key); }
    t.subs.get(key)!.push(v);
  }
  // 라이브러리 탭과 같은 순서 — 기본동작 → 태극 → 나머지 품새(재생목록 순서: 태백·금강·고려…).
  // 예전엔 생성일 순서 그대로라 도장 기본동작(먼저 만들어짐)이 맨 아래·태극이 품새 뒤로 가 라이브러리와 거꾸로 보였다.
  const pin = (t: string) => (t === "기본동작" ? 0 : t === "태극" ? 1 : 2);
  topOrder.sort((a, b) => pin(a) - pin(b)); // 안정 정렬 — 같은 칸 안은 재생목록 순서 유지
  const result: RefGroup[] = [];
  for (const top of topOrder) {
    const t = tops.get(top);
    if (!t) continue;
    const subs = t.subOrder.slice().sort((a, b) => (subRank.get(top + "/" + a) ?? 1e9) - (subRank.get(top + "/" + b) ?? 1e9)).map((key) => ({
      label: key || null,
      videos: t.subs.get(key)!.slice().sort((a, b) =>
        refOrderKey(a) - refOrderKey(b) || (posOf.get(a.id) ?? 1e9) - (posOf.get(b.id) ?? 1e9)), // 전체→품마디1~N 오름차순
    }));
    if (top === "태극") subs.sort((a, b) => taegeukNo(a.label || "") - taegeukNo(b.label || "")); // 1장→8장 오름차순
    result.push({ label: top, subs, count: subs.reduce((n, s) => n + s.videos.length, 0) });
  }
  if (etc.length) result.push({ label: "기타", subs: [{ label: null, videos: etc }], count: etc.length });
  return result;
}

// 영상 추가 시트의 한 줄 — 탭하면 '고른다'(담기는 하단 버튼에서 한 번에). 공용 GlassRow(부모 GlassGroup 안)
function AddVideoRow({ v, selected, onToggle }: { v: VideoView; selected: boolean; onToggle: (id: string) => void }) {
  const locale = useLocale();
  return (
    <GlassRow
      thumb={<Poster video={v} w="100%" h="100%" radius={0} showLabel={false} showDur={false} />}
      title={tkdTitle(v.title, locale)}
      sub={<span className="row gap-3 tnum muted" style={{ alignItems: "center", fontSize: 12.5 }}><I.clock size={12.5} />{fmt(v.durationSec)}</span>}
      trailing={
        <span aria-hidden style={{
          width: 22, height: 22, borderRadius: "50%", flex: "none",
          display: "grid", placeItems: "center",
          // iOS 체크: 안 고른 원은 얇은 실선(1.5px)으로 조용히, 고른 원은 액센트로 채우고 흰 체크.
          // 굵은 2px + 굵은 획(sw 3)은 목록 오른쪽 끝에서 과하게 튀었다.
          border: selected ? "1.5px solid var(--accent)" : "1.5px solid var(--border)",
          background: selected ? "var(--accent)" : "transparent",
          boxShadow: selected ? "var(--shadow-1)" : "none",
          color: "#fff",
          transition: "background .14s ease, border-color .14s ease, box-shadow .14s ease",
        }}>{selected && <I.check size={13.5} sw={2.4} />}</span>
      }
      onClick={() => onToggle(v.id)}
    />
  );
}

/* 품새(장) 하나의 '영상 추가' 몸통 — [전체 | 품마디 | 품 | 낱기술] 탭(있는 것만), 품마디 탭 안에서 각도(정면/후면/부감),
   낱기술 탭 안에서 종류(차기/서기/수기) 선택. FormDrill 과 동일 구성이되 재생이 아니라 +추가(AddVideoRow).
   품·낱기술은 DataCtx.breakdown 으로 이 시트에만 온다(2026-09-15). 검색 중(flat)이거나 탭거리가 없으면 평면 리스트로. */
function AddRefBody({ videos, sel, onToggle, flat }: { videos: VideoView[]; sel: Set<string>; onToggle: (id: string) => void; flat?: boolean }) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  // 구령(하나둘/손발손) — 허용 계정(DataCtx.sbs)만 전체·품마디 탭에 칩이 보이고, 고른 구령만 목록에 뜬다(드릴 D안과 같은 모양).
  const { sbs } = useData();
  const [pickSbsRaw, setPickSbs] = useState(false);
  const [seg, setSeg] = useState<"whole" | "machi" | "poom" | "tech">("whole");
  // 이 품새·이 탭에 손발손 영상이 없으면 칩을 숨기고 하나둘로 보인다.
  const sbsHere = seg === "machi" ? videos.some((v) => isMachiV(v) && isSbsV(v))
    : seg === "whole" ? videos.some((v) => !isMachiV(v) && !isPoomV(v) && !isTechV(v) && isSbsV(v)) : false;
  const showSbs = !!sbs && sbsHere;
  const pickSbs = showSbs && pickSbsRaw;
  const inVer = (v: VideoView) => isSbsV(v) === pickSbs;
  const machisAll = videos.filter(isMachiV);
  const machis = machisAll.filter(inVer);
  const pooms = videos.filter(isPoomV).sort(bySortV);
  const techs = videos.filter(isTechV);
  const wholes = videos.filter((v) => !isMachiV(v) && !isPoomV(v) && !isTechV(v) && inVer(v));
  const vpsPresent = REF_VP.filter((x) => machisAll.some((v) => (v.refViewpoint ?? "") === x));
  const techKinds = [
    ...TECH_KINDS.filter((k) => techs.some((v) => techKindV(v) === k)),
    ...[...new Set(techs.map(techKindV))].filter((k) => !TECH_KINDS.includes(k)).sort(),
  ];
  const [vp, setVp] = useState<string | null>(null); // null=미선택(첫 present 각도로 폴백)
  const [tech, setTech] = useState<string | null>(null); // null=미선택(첫 present 종류로 폴백)
  // 검색 중이거나 탭거리 없음 → 탭 없이 평면(검색어가 어느 탭에 있든 다 보이게).
  if (flat || (machisAll.length === 0 && pooms.length === 0 && techs.length === 0))
    return <GlassGroup glass inset={97}>{videos.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={onToggle} />)}</GlassGroup>;
  const curVp = vp && vpsPresent.includes(vp) ? vp : (vpsPresent[0] ?? "정면");
  const machiVids = machis
    .filter((v) => (v.refViewpoint ?? "") === curVp)
    .sort((a, b) => machiNoV(a) - machiNoV(b) || (a.refSort ?? 0) - (b.refSort ?? 0));
  const curTech = tech && techKinds.includes(tech) ? tech : (techKinds[0] ?? "");
  const techVids = techs.filter((v) => techKindV(v) === curTech).sort(bySortV);
  const chip = (on: boolean) => ({
    fontSize: 13, padding: "6px 14px", borderRadius: "var(--r-lg)", cursor: "pointer",
    border: "1px solid " + (on ? "var(--accent)" : "var(--border)"),
    background: on ? "var(--accent)" : "transparent",
    color: on ? "#fff" : "var(--text-2)", fontWeight: on ? 600 : 400,
  } as const);
  // 구령 칩 — 드릴과 같은 자리(품마디는 각도 칩 밑, 전체는 탭 밑).
  const verChips = (
    <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
      <span className="muted" style={{ fontSize: 13 }}>{tm("addref.ver")}</span>
      <button onClick={() => setPickSbs(false)} style={chip(!pickSbs)}>{tm("addref.hana")}</button>
      <button onClick={() => setPickSbs(true)} style={chip(pickSbs)}>{tm("addref.sbs")}</button>
    </div>
  );
  return (
    <div className="col gap-10">
      <Segmented value={seg} onChange={(v) => setSeg(v as "whole" | "machi" | "poom" | "tech")} options={[
        { value: "whole", label: tm("addref.all") },
        ...(machisAll.length > 0 ? [{ value: "machi", label: tm("addref.machi") }] : []),
        ...(pooms.length > 0 ? [{ value: "poom", label: tm("addref.poom") }] : []),
        ...(techs.length > 0 ? [{ value: "tech", label: tm("addref.tech") }] : []),
      ]} />
      {showSbs && seg === "whole" && verChips}
      {seg === "whole" ? (
        wholes.length === 0
          ? <div className="muted" style={{ fontSize: 13, padding: "8px 2px" }}>{pickSbs ? tm("addref.noSbs") : tm("addref.noFull")}</div>
          : <GlassGroup glass inset={97}>{wholes.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={onToggle} />)}</GlassGroup>
      ) : seg === "poom" ? (
        // 품(조합동작) — 시점 없이 refSort 순 한 줄 목록(왼/오 2본씩)
        <GlassGroup glass inset={97}>{pooms.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={onToggle} />)}</GlassGroup>
      ) : seg === "tech" ? (
        // 낱기술 — 종류(차기/서기/수기) 칩(각도 칩과 동일 디자인) + 그 종류의 낱개기술 목록
        <div className="col gap-10">
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <span className="muted" style={{ fontSize: 13 }}>{tm("addref.kind")}</span>
            {techKinds.map((x) => (
              <button key={x} onClick={() => setTech(x)} style={chip(curTech === x)}>{tkdTechKind(x, locale)}</button>
            ))}
          </div>
          {techVids.length === 0
            ? <div className="muted" style={{ fontSize: 13, padding: "8px 2px" }}>{tm("addref.noKind")}</div>
            : <GlassGroup glass inset={97}>{techVids.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={onToggle} />)}</GlassGroup>}
        </div>
      ) : (
        <div className="col gap-10">
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <span className="muted" style={{ fontSize: 13 }}>{tm("addref.angle")}</span>
            {vpsPresent.map((x) => (
              <button key={x} onClick={() => setVp(x)} style={chip(curVp === x)}>{x}</button>
            ))}
          </div>
          {showSbs && verChips}
          {machiVids.length === 0
            ? <div className="muted" style={{ fontSize: 13, padding: "8px 2px" }}>{pickSbs ? tm("addref.noSbs") : tm("addref.noAngle")}</div>
            : <GlassGroup glass inset={97}>{machiVids.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={onToggle} />)}</GlassGroup>}
        </div>
      )}
    </div>
  );
}

/* ---------- Add-video sheet (재생목록에 영상 추가 — 탭+검색) ----------
   avail(아직 안 담긴 영상)만 받아 라이브러리와 동일한 '내 영상/기본·품새' 탭·검색 제공.
   기본·품새 탭은 재생목록별 섹션으로 묶어 보여준다(태극은 한 그룹). 내 영상 탭은 평면 목록.
   탭 전환·검색으로 목록량이 변해도 시트가 출렁이지 않게 height 고정. */
export function AddVideoSheet({ avail, onAddMany, onClose, onUpload, mineOnly }: {
  avail: VideoView[];
  onAddMany: (ids: string[]) => void; // 고른 것들을 '한 번에' 담는다(고른 순서대로)
  onClose: () => void;
  onUpload?: () => void; // 시트 맨 아래 '새 영상 업로드' — 업로드 후 이 재생목록에 자동 추가
  mineOnly?: boolean; // 기본/품새 재생목록에 덧붙이기 — '내 영상'만 추가 가능(제공 영상은 대상 아님)
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const { playlists, byId } = useData();
  // 내 재생목록에 담을 땐 '내 영상' 탭부터 연다 — 내가 만든 목록인데 기본·품새부터 열려서 매번 탭을
  // 바꿔야 했다. 단, 도장에 내 영상이 하나도 없으면 빈 탭이 열려 더 답답하므로 그때만 기본·품새로.
  const hasMine = avail.some((v) => !isReferenceVideo(v));
  const f = useVideoFilter(avail, mineOnly || hasMine ? "mine" : "ref");
  const groups = !mineOnly && f.tab === "ref" ? groupReference(f.filtered, playlists, byId) : null;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // 다중선택 — 행을 탭하면 고르기만 하고, 저장은 하단 '담기' 한 번. 예전엔 탭 한 번이 곧 저장이라
  // 5개 담으면 서버 왕복 10회(PUT+GET ×5)에 목록이 손가락 밑에서 밀렸다.
  const [picked, setPicked] = useState<string[]>([]); // 고른 순서 유지 = 담기는 순서
  const sel = new Set(picked);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const commit = () => { if (picked.length) { onAddMany(picked); setPicked([]); onClose(); } };
  return (
    <SheetShell onClose={onClose} height="80%">
      <div className="sec-head" style={{ marginBottom: 12, alignItems: "center", gap: 8 }}>
        {/* 제목(주) → 개수(보조 정보) → 업로드(보조 액션). 개수는 제목에 붙여 한 덩어리로 읽히게 하고,
            크기·굵기·색을 한 단계씩 낮춰 계층이 눈에 보이게 한다. */}
        <div className="row" style={{ alignItems: "baseline", gap: 6, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}>{tm("sheet.addVideos")}</span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-3)" }}>
            {picked.length ? tm("count.selected", { count: picked.length }) : tm("count.n", { count: avail.length })}
          </span>
        </div>
        {/* 업로드는 보조 액션 — 이 시트의 주인공은 '고르기'다. 그래서 채운 알약이 아니라
            라이브러리 섹션 헤더(secHead)와 같은 '액센트 텍스트 버튼'으로 조용히 둔다. */}
        {onUpload && (
          <button onClick={onUpload} className="row gap-4" style={{
            background: "none", border: "none", cursor: "pointer", color: "var(--accent)",
            fontWeight: 700, fontSize: 13, alignItems: "center", flex: "none", padding: 0,
          }}>
            <I.upload size={15} />{tm("common.upload")}
          </button>
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        {mineOnly ? (
          <div className="vsearch">
            <I.search size={17} className="dim" />
            <input className="vsearch__input" value={f.q} onChange={(e) => f.setQ(e.target.value)}
              placeholder={tm("sheet.searchMyTitle")} inputMode="search" />
            {f.q && <button className="vsearch__clear" onClick={() => f.setQ("")} aria-label={tm("common.clearSearch")}><I.x size={14} sw={2.2} /></button>}
          </div>
        ) : (
          <VideoFilterBar tab={f.tab} setTab={f.setTab} q={f.q} setQ={f.setQ} counts={f.counts} />
        )}
      </div>
      <div className="col gap-8">
        {f.filtered.length === 0 && (
          <div className="col gap-12" style={{ padding: "8px 2px", alignItems: "flex-start" }}>
            <span className="muted" style={{ fontSize: 13 }}>
              {f.query ? tm("common.noResults", { q: f.q.trim() })
                : f.tab === "mine" ? tm("sheet.noVideosToAdd")
                : tm("sheet.noProvidedToAdd")}
            </span>
            {/* 찾는 영상이 없다 = 올릴 때다. 흐름이 여기서 끊기지 않게 그 자리에서 이어준다. */}
            {onUpload && f.tab === "mine" && (
              <button onClick={onUpload} className="btn btn--primary" style={{ height: 42, padding: "0 16px", width: "auto" }}>
                <I.upload size={17} />{tm("sheet.uploadNew")}
              </button>
            )}
          </div>
        )}
        {groups
          ? groups.map((g) => {
              // 검색 중엔 전부 펼쳐 결과를 바로 보여주고, 평소엔 접어 두고 탭하면 펼친다.
              const isOpen = !!f.query || !!open[g.label];
              return (
                <div key={g.label} className="col gap-8">
                  <button onClick={() => setOpen((s) => ({ ...s, [g.label]: !s[g.label] }))} className="row gap-8" style={{
                    width: "100%", textAlign: "left", background: "var(--surface-1)", border: "1px solid var(--border)",
                    borderRadius: "var(--r-md)", padding: "11px 12px", cursor: "pointer",
                  }}>
                    <span style={{ color: "var(--text-3)", display: "flex" }}>{isOpen ? <I.chevD size={18} /> : <I.chevR size={18} />}</span>
                    <span style={{ fontSize: 15, fontWeight: 800, flex: 1 }}>{tkdRefGroup(g.label, locale)}</span>
                    <span className="muted" style={{ fontSize: 12.5 }}>{tm("count.n", { count: g.count })}</span>
                  </button>
                  {isOpen && g.subs.map((s) => {
                    // 하위 라벨이 있는 그룹(기본동작=지역, 태극=장)은 한 번 더 접어 둔다 — 탭하면 그 묶음 영상만 펼침.
                    if (s.label) {
                      const subKey = g.label + "/" + s.label;
                      const subOpen = !!f.query || !!open[subKey];
                      return (
                        <div key={s.label} className="col gap-8" style={{ paddingLeft: 8 }}>
                          <button onClick={() => setOpen((st) => ({ ...st, [subKey]: !st[subKey] }))} className="row gap-8" style={{
                            width: "100%", textAlign: "left", background: "var(--surface-1)", border: "1px solid var(--border)",
                            borderRadius: "var(--r-md)", padding: "9px 11px", cursor: "pointer",
                          }}>
                            <span style={{ color: "var(--text-3)", display: "flex" }}>{subOpen ? <I.chevD size={16} /> : <I.chevR size={16} />}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{tkdRefSub(s.label, locale)}</span>
                            <span className="muted" style={{ fontSize: 12 }}>{tm("count.n", { count: s.videos.length })}</span>
                          </button>
                          {subOpen && (
                            <div style={{ paddingLeft: 6 }}>
                              <AddRefBody videos={s.videos} sel={sel} onToggle={toggle} flat={!!f.query} />
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={s.label ?? "_"} className="col gap-8" style={{ paddingLeft: 8 }}>
                        {s.label && <div className="muted" style={{ fontSize: 12.5, fontWeight: 700, padding: "2px 2px 0" }}>{tkdRefSub(s.label, locale)}</div>}
                        <AddRefBody videos={s.videos} sel={sel} onToggle={toggle} flat={!!f.query} />
                      </div>
                    );
                  })}
                </div>
              );
            })
          : <GlassGroup glass inset={97}>{f.filtered.map((v) => <AddVideoRow key={v.id} v={v} selected={sel.has(v.id)} onToggle={toggle} />)}</GlassGroup>}
      </div>
      {/* 고른 게 있을 때만 뜨는 담기 바 — 목록을 스크롤해도 시트 아래에 붙어 있다 */}
      {picked.length > 0 && (
        <div className="sheet__bar">
          <button className="btn btn--ghost btn--lg" onClick={() => setPicked([])}>{tm("sheet.clearSel")}</button>
          <button className="btn btn--lg btn--primary" onClick={commit}>{tm("count.addN", { count: picked.length })}</button>
        </div>
      )}
    </SheetShell>
  );
}

/* ---------- 재생목록에 담기 (라이브러리 미리보기 '＋ 담기' → 이 영상을 담을 재생목록 고르기) ----------
   재생목록이 많아도 검색 + 스크롤로 견딤. 맨 위 '새 재생목록'은 이름 입력 후 바로 담긴다. */
export function PlaylistPickerSheet({ videoId, onAdd, onCreateAndAdd, onClose }: {
  videoId: string;
  onAdd: (plId: string) => void;          // 기존 재생목록에 담기
  onCreateAndAdd: (name: string) => void; // 새 재생목록 만들고 담기
  onClose: () => void;
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const { playlists, byId } = useData();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  // 담을 영상이 이 도장 소유(내 업로드)면 기본/품새(제공) 목록에도 덧붙일 수 있다(도장 오버라이드).
  // 반면 마스터 라이브러리 제공 클립(레퍼런스·byId 에 없는 세부 품/낱기술 포함)은 서버가 기본 목록 덧붙이기를
  // '도장 소유 영상'으로만 허용하므로, 담을 대상은 '내 재생목록'뿐이다(PUT 은 마스터 refForm 을 받아줌).
  const own = !!byId[videoId] && !isReferenceVideo(byId[videoId]);
  const mine = own ? playlists : playlists.filter((pl) => !pl.isDefault);
  const query = q.trim().toLowerCase();
  const shown = query ? mine.filter((pl) => pl.name.toLowerCase().includes(query)) : mine;
  const submitNew = () => { const n = name.trim(); if (n) onCreateAndAdd(n); };
  return (
    <SheetShell onClose={onClose} height="80%">
      <div className="sec-head" style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 17, fontWeight: 800 }}>{tm("sheet.addToPlaylist")}</span>
        <span className="muted" style={{ fontSize: 13 }}>{tm("count.n", { count: mine.length })}</span>
      </div>
      {/* 목록이 길 때만 검색 노출 — 이름으로 좁혀 스크롤 부담 제거 */}
      {mine.length > 6 && (
        <div className="vsearch" style={{ marginBottom: 12 }}>
          <I.search size={17} className="dim" />
          <input className="vsearch__input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tm("sheet.searchPlName")} inputMode="search" />
          {q && <button className="vsearch__clear" onClick={() => setQ("")} aria-label={tm("common.clearSearch")}><I.x size={14} sw={2.2} /></button>}
        </div>
      )}
      <div className="col gap-8">
        {/* 새 재생목록 — 상단 고정 */}
        {creating ? (
          <div className="row gap-8">
            <input autoFocus className="input" style={{ height: 46, flex: 1 }} value={name}
              onChange={(e) => setName(e.target.value)} placeholder={tm("sheet.newPlName")}
              onKeyDown={(e) => { if (e.key === "Enter") submitNew(); }} />
            <button className="btn btn--primary" style={{ height: 46, width: "auto", padding: "0 16px" }} onClick={submitNew} disabled={!name.trim()}>{tm("common.create")}</button>
          </div>
        ) : (
          <button onClick={() => setCreating(true)} className="row gap-10" style={{
            width: "100%", textAlign: "left", background: "var(--accent-soft)", border: "1px dashed var(--accent-line)",
            borderRadius: "var(--r-md)", padding: 12, cursor: "pointer", color: "var(--accent-ink)",
          }}>
            <I.plus size={19} /><span style={{ fontWeight: 700, fontSize: 15 }}>{tm("sheet.createNew")}</span>
          </button>
        )}
        {shown.length === 0 && !creating && (
          <div className="muted" style={{ fontSize: 13, padding: "8px 2px" }}>
            {query ? tm("common.noResults", { q: q.trim() }) : tm("sheet.noMyPl")}
          </div>
        )}
        {shown.length > 0 && (
          <GlassGroup glass inset={71}>
            {shown.map((pl) => {
              const has = pl.items.includes(videoId);
              return (
                <GlassRow key={pl.id}
                  lead={<span style={{ width: 40, height: 40, borderRadius: "var(--r-sm)", background: "var(--surface-2)", display: "grid", placeItems: "center", flex: "none", color: "var(--text-2)" }}><I.layers size={19} /></span>}
                  title={tkdPlaylist(pl.name, locale)}
                  sub={<span className="muted" style={{ fontSize: 12.5 }}>{tm("count.videos", { count: pl.items.length })}</span>}
                  trailing={has
                    ? <span className="row gap-4" style={{ color: "var(--accent)", fontSize: 13, fontWeight: 700, flex: "none" }}><I.check size={16} sw={2.4} />{tm("sheet.added")}</span>
                    : <I.plus size={20} style={{ color: "var(--text-3)", flex: "none" }} />}
                  onClick={has ? undefined : () => onAdd(pl.id)}
                  style={has ? { opacity: 0.75 } : undefined}
                />
              );
            })}
          </GlassGroup>
        )}
      </div>
    </SheetShell>
  );
}

/* ---------- 자습 범위 시트 ----------
   '무엇을 틀까요?'(LoadSheet)와 같은 얼개(2026-09-04 사용자: "자습 범위 시트도 같은 템플릿으로") — 제목·오른쫑 학생 이름,
   썸네일 줄, 지금 것은 칠해진 행+체크, 나머지는 화살표. 맨 위 '모두'·'아직 품새 전' 두 줄, 그 아래 '품새 · N' 묶음.
   🛑 ActionSheet(큰 상자·아이콘 줄)로 되돌리지 말 것. */
export function RangeSheet({ studentName, forms, current, onPick, onClose }: {
  studentName: string;
  forms: MasterForm[];              // 마스터 카탈로그 순서(국기원 순서) 그대로 — 기본동작은 여기서 뺀다
  current: string | null;           // Device.rankFormId — null=모두, RANK_BASICS=아직 품새 전, 그 외 품새 id
  onPick: (rankFormId: string | null) => void;
  onClose: () => void;
}) {
  const tp = useTranslations("control.page");
  const locale = useLocale();
  const { playlists, byId } = useData();
  const poomsae = forms.filter((f) => f.category !== "기본동작");
  // 썸네일 — 그 품새와 이름이 같은 도장 재생목록의 첫 영상(LoadSheet 가 품새 줄에 쓰는 것과 같은 그림).
  const thumbOf = (name: string) => {
    const pl = playlists.find((p) => p.name === name) ?? null;
    const v = pl ? byId[pl.items[0]] : undefined;
    return v ? <Poster video={v} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />;
  };
  const basicsPl = playlists.find(isBasic);
  const basicsThumb = basicsPl && byId[basicsPl.items[0]]
    ? <Poster video={byId[basicsPl.items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} />
    : <I.layers size={20} />;
  const mark = (on: boolean) => on
    ? <I.check size={18} sw={2.4} style={{ color: "var(--accent)", flex: "none" }} />
    : <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />;
  const row = (key: string, thumb: ReactNode, title: string, on: boolean, pick: () => void) => (
    <GlassRow key={key} thumb={thumb} title={title} active={on} trailing={mark(on)} onClick={pick} />
  );
  return (
    <SheetShell onClose={onClose} height="80%">
      <div className="sec-head" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 800 }}>{tp("dialog.rangeTitle")}</span>
        <span className="muted" style={{ fontSize: 13 }}>{studentName}</span>
      </div>
      <div className="col gap-12">
        {/* 위엔 '모두' 한 줄만. '아직 품새 전'은 사다리 첫 칸 '기본동작까지'로 내려갔다(2026-09-04 사용자 1번 선택 —
            '모두'와 나란히 서 있으니 같은 종류처럼 읽혔다). */}
        <GlassGroup glass inset={97}>
          {row("all", <I.layers size={20} />, tp("range.all"), current == null, () => onPick(null))}
        </GlassGroup>
        <div className="row" style={{ alignItems: "center", justifyContent: "space-between", gap: 8, margin: "-2px 2px -4px 6px" }}>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--text-3)", letterSpacing: ".04em" }}>
            {tp("range.ladder")} · {poomsae.length + 1}
          </span>
        </div>
        <GlassGroup glass inset={97}>
          {row("basics", basicsThumb, tp("range.upTo", { form: tp("range.basicsName") }), current === RANK_BASICS, () => onPick(RANK_BASICS))}
          {poomsae.map((f) => row(f.id, thumbOf(f.name), tp("range.upTo", { form: tkdPlaylist(f.name, locale) }), current === f.id, () => onPick(f.id)))}
        </GlassGroup>
      </div>
    </SheetShell>
  );
}

/* ---------- Playlist detail (reorderable + add/remove, persisted via onPersist) ---------- */
const PL_GAP = 9; // .col gap-9 와 동일해야 드래그 계산이 맞음

/* ---------- 한 벌 미리 받아두기 ---------- */
/* ⭐ 2026-08-27 자리 이동(사용자: "너무 지저분하니까 더보기에 넣어놔").
   버튼('이 목록 받아두기')과 완료 표시('받아둠')는 상단 ⋯ 메뉴 안으로 들어갔다 —
   목록 화면 맨 위를 늘 차지하던 44px 버튼이 사라진다.
   🛑 화면에 남는 건 **받는 중일 때의 진행 줄 하나뿐**이다. 이건 메뉴로 못 옮긴다:
      받는 동안 시트를 닫으면 뭐가 도는지 볼 데가 없어진다('그만'도 거기 있다).
   🛑 제공 목록(isDefault)엔 원래 ⋯ 가 없었다 → 같이 띄웠다. 안 그러면 정작 제일 많이
      받아둘 품새 목록에서 받아두기에 닿을 길이 끊긴다. */
/* 회선이 나쁜 도장·부스에서 '오늘 쓸 것'이 확실히 돌게 한다. 전집(실측 1.6GB)이 아니라 이 목록만.
   받는 화질은 이 기기 화면 크기를 따른다(lib/mirror-quality.ts) — 폰은 360p, 태블릿·PC 는 480p.
   제어화면 상세가 그 화질로 재생하므로 버튼도 같은 것을 받아야 헛되지 않는다.
   그리드 타일(360p)은 프리페치 대상이 아니다 — 8개 동시 480p 디코딩이 타일을 360p 로 낮춘 이유의 절반.
   진행 상태는 모듈에 있어서 화면을 떠났다 돌아와도 이어서 보인다. */
const asMB = (b: number) => (b <= 0 ? 0 : Math.max(1, Math.round(b / 1048576))); // 0 은 0 — 시작하자마자 1MB 라고 하면 거짓말

/** 이 목록의 받아두기 셈 — 버튼(⋯ 메뉴)과 진행 줄이 같이 쓴다. */
export function useSetPlan(items: SetItem[]): SetPlan | null {
  const [plan, setPlanState] = useState<SetPlan | null>(null);
  const st = usePrefetch();
  const sig = items.map((it) => `${it.rel}|${it.tag ?? ""}`).join(",");
  const running = !!st?.running;
  useEffect(() => {
    let live = true;
    if (!items.length) { setPlanState(null); return; }
    void setPlan(items).then((p) => { if (live) setPlanState(p); });
    return () => { live = false; };
    // 받기가 끝나면(running false 로 떨어지면) 다시 세어 '받아둠'으로 바꾼다
  }, [sig, running]); // eslint-disable-line react-hooks/exhaustive-deps
  return plan;
}

function SetPrefetchBar() {
  const tm = useTranslations("control.media");
  const st = usePrefetch();
  if (!st?.running) return null; // 쉬고 있을 때는 화면에서 완전히 사라진다

  {
    const pct = st!.total ? (st!.done / st!.total) * 100 : 0;
    return (
      <div className="col gap-7" style={{ padding: "2px 0 2px" }}>
        <div className="row gap-10" style={{ alignItems: "center" }}>
          <I.download size={16} style={{ color: "var(--accent)", flex: "none" }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>
            {tm("offline.saving", { done: st!.done, total: st!.total, mb: asMB(st!.bytes) })}
          </span>
          <button className="btn btn--ghost" style={{ height: 30, width: "auto", padding: "0 12px", fontSize: 13 }} onClick={cancelPrefetch}>
            {tm("offline.stop")}
          </button>
        </div>
        {/* 첫 한 편이 끝나기 전엔 0% 막대가 멈춘 것처럼 보인다 → 그동안은 흐르는 막대로 */}
        <Track value={pct} h={4} indeterminate={st!.done === 0} />
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.45 }}>{tm("offline.savingHint")}</div>
      </div>
    );
  }
}

export function PlaylistDetail({ playlist, onClose, onLoadToDevice, onMenu, onPersist, onUpload, onPlayItem, startPicking, returnDeviceName, onToggleHidden, onAddDefault, onRemoveDefault, onReorderDefault }: {
  playlist: PlaylistView;
  onClose: () => void;
  onLoadToDevice: () => void;
  /** 상단 ⋯ — '이 목록 받아두기'가 이 안으로 들어갔다(2026-08-27). 셈(plan)과 대상(items)을 같이 넘긴다. */
  onMenu: (offline?: { items: SetItem[]; plan: SetPlan | null }) => void;
  onPersist: (items: string[]) => Promise<boolean>;
  onUpload?: () => void; // '영상 추가' 시트 맨 아래 업로드 — 완료 시 이 재생목록에 자동 추가
  onPlayItem?: (ids: string[], index: number) => void; // 항목 탭 시 기기 없이 로컬(제어화면 룩)로 미리보기 재생. 낙관적 순서(items) 그대로 전달
  startPicking?: boolean; // 진입과 동시에 '영상 추가' 시트(영상 목록) 자동 열기 — 제어 화면 큐의 영상 추가
  returnDeviceName?: string | null; // 기기에서 진입했으면 그 기기명 — 하단 CTA를 '○○에서 재생'으로(기기 선택 팝업 생략)
  // 기본/품새(isDefault) 재생목록 도장별 커스터마이즈 — 오버라이드 콜백(서버 반영 후 목록 새로고침).
  onToggleHidden?: (videoId: string, hidden: boolean) => void; // 제공 영상 눈알 토글(재생 숨김/해제)
  onAddDefault?: (videoIds: string[]) => void; // 여러 개를 한 번에    // 내 영상 덧붙이기
  onRemoveDefault?: (videoId: string) => void; // 내가 덧붙인 영상 빼기
  onReorderDefault?: (videoIds: string[]) => void; // 기본/품새 재생목록 도장별 순서 저장
}) {
  const tm = useTranslations("control.media");
  const locale = useLocale();
  const { videos, byId, breakdown } = useData();
  const mq = useMirrorQuality();
  const isDefault = playlist.isDefault; // 기본 제공(기본동작/품새) 재생목록 — 제공 영상은 삭제 대신 눈알로 숨김만
  // 커스텀 재생목록은 낙관적 순서(items)로 드래그 정렬; 기본 재생목록은 서버 병합 결과(추가/숨김 반영)를 따른다.
  const [items, setItems] = useState<string[]>(playlist.items.slice());
  useEffect(() => { setItems(playlist.items.slice()); }, [playlist]);
  // 눈알(재생 숨김) 낙관적 오버레이 — 토글 즉시 카드 반투명, 새로고침으로 확정.
  const [hiddenOv, setHiddenOv] = useState<Record<string, boolean>>(playlist.hidden);
  useEffect(() => { setHiddenOv(playlist.hidden); }, [playlist]);
  const [pick, setPick] = useState(() => !!startPicking);

  // 커스텀 재생목록: 화면 즉시 반영, 저장 실패 시 이전 순서로 복귀 (실패 토스트는 onPersist 쪽 책임)
  const commit = (next: string[]) => {
    const prev = items;
    setItems(next);
    onPersist(next).then((ok) => { if (!ok) setItems(prev); });
  };
  // 순서변경(드래그): 커스텀=PUT 전체치환, 기본/품새=도장별 순서 오버라이드 저장(제공+추가 통합 순서).
  const grip = useGripReorder(items.length, PL_GAP, (from, to) => {
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    if (isDefault) { setItems(next); onReorderDefault?.(next); } // 낙관적 + 저장(새로고침으로 확정)
    else commit(next);
  });
  const addMany = (ids: string[]) => { if (isDefault) onAddDefault?.(ids); else commit([...items, ...ids]); };
  const removeCustom = (id: string) => commit(items.filter((x) => x !== id));
  const toggleHide = (id: string) => {
    const nv = !hiddenOv[id];
    setHiddenOv((s) => ({ ...s, [id]: nv }));
    onToggleHidden?.(id, nv);
  };
  // 추가 가능 영상 — 기본 재생목록엔 '내 영상'만 덧붙일 수 있다(제공 영상은 대상 아님).
  // 품·낱기술(breakdown)은 이 '영상 추가' 시트에서만 담을 수 있다(라이브러리엔 안 뜸) — 내 재생목록일 때만.
  const avail = [...videos, ...(isDefault ? [] : breakdown)].filter((v) => !items.includes(v.id) && (!isDefault || !isReferenceVideo(v)));
  useEffect(() => { if (pick && avail.length === 0 && !onUpload) setPick(false); }, [pick, avail.length, onUpload]);
  const dur = items.reduce((a, id) => a + (byId[id]?.durationSec || 0), 0);
  const canReorder = isDefault ? !!onReorderDefault : true; // 기본/품새도 순서 저장 콜백 있으면 재정렬 허용
  // 미리 받아둘 것 — 재생에서 숨기지 않은, 미러본 있는 영상. 제공 목록·내가 만든 목록 모두 같다.
  // 480p 가 25MB(4분)를 넘으면 캐시 대상에서 빠지므로 360p 대체본을 함께 준다.
  const offline: SetItem[] = items
    .map((id) => byId[id])
    .filter((v): v is VideoView => !!v && !hiddenOv[v.id] && !!(v.mirror || (mq.hiCache && v.mirrorHi)))
    .map((v) => (mq.hiCache && v.mirrorHi
      ? { rel: v.mirrorHi, tag: v.mirrorHiTag ?? null, fb: v.mirror ? { rel: v.mirror, tag: v.mirrorTag ?? null } : null }
      : { rel: v.mirror as string, tag: v.mirrorTag ?? null }));
  const offlinePlan = useSetPlan(offline);
  // 줄마다 훅을 부르지 않는다 — 목록 하나에 수십 번이라 여기서 한 번 받아 나눠 쓴다.
  const cachedKeys = useCachedKeys();
  // ⋯ 를 띄울 이유가 있나 — 커스텀 목록은 이름변경·삭제가 있고, 제공 목록은 받아두기가 있을 때만.
  const hasMenu = !isDefault || (offline.length > 0 && !!offlinePlan);

  return (
    <div className="screen col" style={{ height: "100%" }}>
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
        <div className="ctl-hd-sticky"><AppBar title={tkdPlaylist(playlist.name, locale)} onBack={onClose} sub={tm("count.videosDur", { count: items.length, dur: fmt(dur) })}
          right={hasMenu ? <button className="iconbtn iconbtn--plain" onClick={() => onMenu({ items: offline, plan: offlinePlan })} aria-label={tm("common.more")}><I.dots size={20} /></button> : undefined} /></div>
        <div className="col gap-9" style={{ padding: "4px 18px 14px" }}>
        {/* 받는 중에만 뜬다 — 평소엔 목록 맨 위가 비어 있다(버튼은 ⋯ 안으로). */}
        <SetPrefetchBar />
        {items.length === 0 && (
          <div className="col gap-10" style={{ alignItems: "center", padding: "40px 20px", color: "var(--text-3)" }}>
            <I.layers size={34} /><span style={{ fontSize: 14 }}>{tm("detail.noVideos")}</span>
          </div>
        )}
        {items.map((id, i) => {
          const v = byId[id];
          if (!v) return null;
          const isAdded = !!playlist.added[id]; // 이 도장이 덧붙인 영상(삭제 가능)
          const isRef = isReferenceVideo(v);    // 우리가 제공한 영상(눈알로 숨김만)
          const isHidden = isDefault && !!hiddenOv[id];
          return (
            <div key={id} data-dragrow={canReorder ? "" : undefined} onPointerDown={canReorder ? grip.onRowDown(i) : undefined}
              onClick={() => { if (!grip.dragging) onPlayItem?.(items, i); }} className="row gap-12" style={{
              background: "var(--glass-bg)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
              border: "1px solid var(--glass-brd)", borderRadius: "var(--r-md)", padding: "10px 12px",
              boxShadow: "var(--glass-shadow-1), inset 0 1px 0 var(--glass-hi-1)",
              cursor: onPlayItem ? "pointer" : undefined,
              opacity: isHidden ? 0.5 : 1, // 재생 숨김 = 반투명(목록엔 남음)
              ...(canReorder ? grip.rowStyle(i) : null),
            }}>
              <span className="mono dim" style={{ width: 16, textAlign: "center", fontSize: 13 }}>{i + 1}</span>
              <MediaSlot w={60} h={40}><Poster video={v} w="100%" h="100%" showLabel={false} showDur={false} radius={0} /></MediaSlot>
              <div className="col gap-2" style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15.5, letterSpacing: "-.01em", color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(v.title, locale)}</div>
                <div className="row gap-3 tnum muted" style={{ alignItems: "center", fontSize: 12.5 }}>
                  <I.clock size={12.5} />{fmt(v.durationSec)}
                  {isSbsV(v) && <SbsTag />}
                  {isHidden && <span style={{ color: "var(--text-3)", fontWeight: 700 }}>{tm("detail.hidden")}</span>}
                  {isAdded && <span style={{ color: "var(--accent)", fontWeight: 700 }}>{tm("detail.addedByMe")}</span>}
                  {isCached(cachedKeys, v) && <SavedMark label={tm("offline.saved")} />}
                </div>
              </div>
              {isDefault ? (
                isAdded ? (
                  // 내가 덧붙인 영상 — 뺄 수 있음
                  <button className="iconbtn iconbtn--plain" style={{ width: 30, height: 30, color: "var(--text-3)" }} onClick={(e) => { e.stopPropagation(); onRemoveDefault?.(id); }} aria-label={tm("detail.removeAria")}><I.trash size={17} /></button>
                ) : isRef ? (
                  // 제공 영상 — 삭제 불가, 눈알로 재생 숨김/해제만
                  <button className="iconbtn iconbtn--plain" style={{ width: 30, height: 30, color: isHidden ? "var(--accent)" : "var(--text-3)" }}
                    onClick={(e) => { e.stopPropagation(); toggleHide(id); }} aria-label={isHidden ? tm("detail.showInPlay") : tm("detail.hideFromPlay")}>
                    {isHidden ? <I.eyeOff size={18} /> : <I.eye size={18} />}
                  </button>
                ) : null
              ) : (
                <button className="iconbtn iconbtn--plain" style={{ width: 30, height: 30, color: "var(--text-3)" }} onClick={(e) => { e.stopPropagation(); removeCustom(id); }} aria-label={tm("detail.removeAria")}><I.trash size={17} /></button>
              )}
              {/* 순서 변경 — 그립 핸들(커스텀 재생목록만). 기본/품새는 제공 순서 유지 → 그립 미노출. */}
              {canReorder && <GripHandle onPointerDown={grip.onGripDown(i)} dragging={grip.dragging} />}
            </div>
          );
        })}
        <button className="btn btn--ghost" style={{ height: 48, marginTop: 4 }} onClick={() => setPick(true)} disabled={avail.length === 0 && !onUpload}>
          <I.plus size={18} />{isDefault ? tm("detail.addMyVideos") : tm("detail.addVideos")}{avail.length === 0 && !onUpload ? tm("detail.allAdded") : ""}
        </button>
        </div>
      </ScrollSolidify>
      <div style={{ padding: "10px 18px 18px", borderTop: "1px solid var(--border)" }}>
        <button className="btn btn--primary btn--lg" onClick={onLoadToDevice} disabled={items.length === 0} style={{ opacity: items.length ? 1 : 0.4 }}>
          {returnDeviceName ? <I.play size={19} /> : <I.cast size={19} />}
          {returnDeviceName ? tm("detail.playOn", { name: returnDeviceName }) : tm("detail.loadToDevice")}
        </button>
      </div>

      {pick && <AddVideoSheet avail={avail} onAddMany={addMany} mineOnly={isDefault} onClose={() => setPick(false)} onUpload={onUpload ? () => { setPick(false); onUpload(); } : undefined} />}
    </div>
  );
}

/* ---------- Pairing ---------- */
/* 카메라로 플레이어 QR 스캔 → 6자리 코드 추출 (HTTPS/보안 컨텍스트 필요) */
function QrScanModal({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const tm = useTranslations("control.media");
  const videoRef = useRef<HTMLVideoElement>(null);
  const cbRef = useRef(onCode);
  const [err, setErr] = useState("");
  useEffect(() => { cbRef.current = onCode; }, [onCode]);
  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();
        const tick = () => {
          if (stopped) return;
          if (ctx && v.readyState >= 2 && v.videoWidth) {
            canvas.width = v.videoWidth;
            canvas.height = v.videoHeight;
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const res = jsQR(img.data, img.width, img.height);
            const m = res?.data ? /(\d{6})/.exec(res.data) : null;
            if (m) { cbRef.current(m[1]); return; }
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch {
        setErr(tm("pairing.cameraFail"));
      }
    })();
    return () => { stopped = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, []);
  return (
    <div className="sheet-wrap">
      <div style={{ position: "absolute", inset: 0, background: "#000", display: "flex", flexDirection: "column" }}>
        <video ref={videoRef} playsInline muted style={{ flex: 1, width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
          <div style={{ width: 220, height: 220, border: "3px solid var(--accent)", borderRadius: "var(--r-lg)", boxShadow: "0 0 0 9999px rgba(0,0,0,.45)" }} />
        </div>
        <div className="row" style={{ position: "absolute", top: 0, left: 0, right: 0, padding: 16, justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>{tm("pairing.scanTitle")}</span>
          <button className="iconbtn" style={{ background: "rgba(0,0,0,.5)", borderColor: "transparent", color: "#fff" }} onClick={onClose} aria-label={tm("pairing.close")}><I.back size={22} /></button>
        </div>
        <div style={{ position: "absolute", bottom: 28, left: 18, right: 18, textAlign: "center", color: err ? "var(--danger)" : "rgba(255,255,255,.8)", fontSize: 13.5, textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>
          {err || tm("pairing.scanHint")}
        </div>
      </div>
    </div>
  );
}

export function PairingScreen({ onClose, onToast, onPaired, allowHome = false, defaultAtHome = false, forms = [], students = [], classes = [], defaultClassId = null, onAddClass }: {
  onClose: () => void;
  onToast: (m: string) => void;
  onPaired: (code: string, name: string, atHome: boolean, rankFormId: string | null, studentId: string | null, studentName: string | null, classId: string | null) => void | Promise<void>;
  // 🚧 '학생 집에 두는 화면' 스위치 노출 — 아직 고민 중인 기능이라 테스트 계정에만(lib/flags homeDeviceAllowed).
  //    서버도 같은 판정으로 막으므로 여기서 감추는 건 '보이지 않게' 하는 몫만 한다.
  allowHome?: boolean;
  // 보이는 범위로 고를 수 있는 품새들(순서 = 배우는 순서). 기본동작은 급수와 무관해 빠져 있다.
  /** 홈워킹 탭에서 들어왔다 — 집 화면 토글을 켠 채 열고 제목도 '집 화면 등록'(09-03). 도장 기기 탭에선 false. */
  defaultAtHome?: boolean;
  forms?: { id: string; name: string }[];
  students?: { id: string; name: string }[];
  /** 반 목록 — 비어 있으면 반 칸이 없다. 새 수련생을 만들 때만 뜻이 있다(등록된 수련생은 이미 반이 있다). */
  classes?: { id: string; name: string }[];
  defaultClassId?: string | null;
  /** 반 칸에서 바로 새 반 만들기 — 있으면 반이 0개여도 반 칸이 보인다. */
  onAddClass?: () => Promise<{ id: string; name: string } | null>;
}) {
  const tm = useTranslations("control.media");
  const tk = useTranslations("control.classes");
  const [classId, setClassId] = useState(defaultClassId && classes.some((c) => c.id === defaultClassId) ? defaultClassId : "");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [atHome, setAtHome] = useState(allowHome && defaultAtHome); // 학생 집에 두는 화면 — 도장 전체 조작에서 빠진다
  const [rankFormId, setRankFormId] = useState(""); // 보일 콘텐츠 상한(품새). ""=제한 없음
  const [studentMode, setStudentMode] = useState<"new" | "existing">("new");
  const [studentId, setStudentId] = useState("");
  const [studentName, setStudentName] = useState("");
  const [scan, setScan] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const selectedStudent = students.find((student) => student.id === studentId);
  const isNewStudent = studentMode === "new" || students.length === 0;
  const studentReady = !atHome || (studentMode === "existing" && students.length > 0 ? !!selectedStudent : !!studentName.trim());
  const full = code.length === 6 && (atHome ? studentReady : name.trim().length > 0);
  // 등록은 잠깐 걸리므로, 급하게 두 번 눌러 기기가 중복 등록되지 않도록 진행 중엔 잠근다.
  const submit = async () => {
    if (submitting || !full) return;
    setSubmitting(true);
    try {
      // 집 화면의 기기 이름은 관장이 따로 짓지 않는다 — 목록에서는 학생 이름으로 읽히고,
      // 이 이름은 수련 기록처럼 '어느 화면이었나'를 되짚을 때만 쓰인다.
      const student = (studentMode === "existing" ? selectedStudent?.name : studentName.trim()) ?? "";
      const initialDeviceName = atHome ? tm("pairing.homeDeviceName", { name: student }) : name.trim();
      await onPaired(code, initialDeviceName, allowHome && atHome, allowHome && atHome && rankFormId ? rankFormId : null,
        allowHome && atHome && studentMode === "existing" ? studentId : null,
        allowHome && atHome && studentMode === "new" ? studentName.trim() : null,
        allowHome && atHome && isNewStudent && classId ? classId : null);
    } finally {
      setSubmitting(false);
    }
  };
  const press = (k: string) => { if (k === "del") setCode((c) => c.slice(0, -1)); else if (code.length < 6) setCode((c) => c + k); };
  // 컴퓨터 키보드로도 친다(2026-09-04 사용자 요청) — 화면 키패드(다이얼)는 그대로 두고, PC 물리 키보드의
  // 숫자·Backspace·Enter 를 문서 전체에서 받는다(다른 입력칸에 초점이 있을 땐 비켜선다 — 이름칸에 숫자 치는 중일 수 있다).
  // 🛑 다이얼을 빼거나 폰 숫자 키보드를 띄우는 투명 input 을 얹지 말 것 — 둘 다 했다가 "다이얼 그대로 둬, 난 컴퓨터
  //    키보드 입력만 말한 건데"로 되돌렸다(같은 날). 다이얼은 폰에서 쓰는 입력기다.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); setCode((c) => (c.length < 6 ? c + e.key : c)); }
      else if (e.key === "Backspace") { e.preventDefault(); setCode((c) => c.slice(0, -1)); }
      else if (e.key === "Enter") { void submitRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="screen col" style={{ height: "100%" }}>
      <AppBar title={tm(defaultAtHome ? "pairing.titleHome" : "pairing.title")} onBack={onClose} />
      <div className="frame__scroll col" style={{ padding: "4px 22px 18px", flex: 1 }}>
        <div className="col gap-6" style={{ marginBottom: 16 }}>
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>{tm.rich("pairing.enterHint", { b: (c) => <b style={{ color: "var(--text-1)" }}>{c}</b> })}</div>
        </div>

        <div className="row gap-8" style={{ justifyContent: "center", marginBottom: 18 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              width: 42, height: 54, borderRadius: 11, display: "grid", placeItems: "center",
              background: "var(--surface-2)", border: "1px solid " + (i === code.length ? "var(--accent-line)" : "var(--border)"),
              fontSize: 24, fontWeight: 800, fontFamily: "var(--mono)",
            }}>{code[i] || ""}</div>
          ))}
        </div>

        {/* 키패드는 **코드칸 바로 밑**에 붙는다. 표시기(6칸)와 입력기는 한 덩어리라 사이에 다른 폼이
            끼면 안 된다 — 예전엔 아래에 있어서, 집 화면 스위치를 켜는 순간 학생·범위 두 덩어리가
            끼어들며 키패드가 화면 밖으로 밀렸다(스위치부터 켜고 코드를 누르려던 사람은 길이 막혔다).
            이제 위쪽 절반은 무엇을 켜든 제자리다. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 18 }}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
            <button key={k} className="btn btn--ghost" style={{ height: 50, fontSize: 20 }} onClick={() => press(k)}>{k}</button>
          ))}
          <button className="btn btn--ghost" style={{ height: 50 }} onClick={() => setScan(true)} aria-label={tm("pairing.scanQr")}><I.qr size={20} /></button>
          <button className="btn btn--ghost" style={{ height: 50, fontSize: 20 }} onClick={() => press("0")}>0</button>
          <button className="btn btn--ghost" style={{ height: 50 }} onClick={() => press("del")} aria-label={tm("pairing.del")}><I.back size={20} /></button>
        </div>

        {/* 집에 두는 화면 — 도장 전체 조작(수업 전체 종료·전체 일시정지/재생·전체 선택)에서 빠진다.
            켜면 터치 잠금도 풀린 채로 만들어진다(집에선 학생이 스스로 골라 보는 게 목적이라). */}
        {allowHome && (
        <div className="row gap-12" style={{
          alignItems: "center", marginBottom: 16, padding: "12px 14px",
          borderRadius: "var(--r-md)", background: "var(--surface-2)", border: "1px solid var(--border)",
        }}>
          <I.home size={19} style={{ flex: "none", color: atHome ? "var(--accent)" : "var(--text-3)" }} />
          <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 15, letterSpacing: "-.01em" }}>{tm("pairing.atHome")}</span>
            <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>{tm("pairing.atHomeDesc")}</span>
          </div>
          <button
            role="switch" aria-checked={atHome} aria-label={tm("pairing.atHome")}
            className="switch" data-on={atHome} onClick={() => setAtHome((v) => !v)} style={{ flex: "none" }}
          >
            <span className="switch__knob" />
          </button>
        </div>
        )}

        {/* 도장 화면일 때만 — 이름은 관장이 부를 이름이다("본관 대형 스크린").
            집 화면이면 이름은 학생 쪽에서 정해지므로 이 칸이 사라진다. */}
        {!atHome && (
          <div className="field" style={{ marginBottom: 14 }}>
            <label>{tm("pairing.deviceName")}</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={tm("pairing.namePh")} />
          </div>
        )}

        {/* 누구네 화면인가 — 집 화면은 기기가 아니라 **학생**에게 매인다. 한 아이가 폰과 PC 를 등록해도
            숙제는 한 벌이어야 해서, 같은 학생으로 이어 두면 진도가 한 곳에 모인다.
            🛑 이미 등록한 학생이 없으면 고르는 칸을 아예 안 보여준다 — 첫 등록에서 빈 목록을 여는 건
               막다른 길이다(고를 게 없는 칸은 사람을 멈춰 세운다). */}
        {allowHome && atHome && (
          <div className="field" style={{ marginBottom: 16 }}>
            <label>{tm("pairing.student")}</label>
            {students.length > 0 && (
              <Segmented value={studentMode} onChange={(v) => setStudentMode(v)} options={[
                { value: "new", label: tm("pairing.studentNew") },
                { value: "existing", label: tm("pairing.studentExisting") },
              ]} />
            )}
            {studentMode === "new" || students.length === 0 ? (
              <input className="input" value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder={tm("pairing.studentNamePh")} />
            ) : (
              <select className="input" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">{tm("pairing.studentPick")}</option>
                {students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
              </select>
            )}
          </div>
        )}

        {/* 어디까지 배웠나 — 고른 품새 위쪽은 학생 화면 목록에서 사라진다(서버가 자른다).
            🛑 관장이 컨트롤러로 보내는 재생은 안 막는다 — 다음 급수를 미리 보여주는 것도 수업이다.
            집 화면일 때만 뜻이 있어 스위치를 켰을 때만 나온다. */}
        {allowHome && atHome && forms.length > 0 && (
          <div className="field" style={{ marginBottom: 16 }}>
            <label>{tm("pairing.rank")}</label>
            <select className="input" value={rankFormId} onChange={(e) => setRankFormId(e.target.value)}>
              <option value="">{tm("pairing.rankAll")}</option>
              {/* 흰띠 — 아직 품새 전. 품새 목록만 있으면 이 상태를 가리킬 항목이 없다. */}
              <option value={RANK_BASICS}>{tm("pairing.rankBasics")}</option>
              {forms.map((f) => <option key={f.id} value={f.id}>{tm("pairing.rankUpTo", { form: f.name })}</option>)}
            </select>
          </div>
        )}

        {/* 반 — 반을 만든 도장에서, 새 수련생을 등록할 때만. 기본은 '반 없음'(안 골라도 등록된다). */}
        {allowHome && atHome && isNewStudent && (classes.length > 0 || onAddClass) && (
          <div className="field" style={{ marginBottom: 16 }}>
            <div className="row" style={{ alignItems: "center", justifyContent: "space-between" }}>
              <label>{tk("field")}</label>
              {/* 새 반 만들기는 목록 밖의 버튼 — 고르는 항목과 만드는 동작이 한 목록에 섞이면 볼품없고 헷갈린다(10-07 사용자). */}
              {onAddClass && (
                <button type="button" onClick={() => void onAddClass().then((c) => { if (c) setClassId(c.id); })}
                  // 글자는 작아도 누르는 자리는 넉넉하게(40px) — 음수 여백으로 줄 높이는 그대로 둔다.
                      style={{ background: "none", border: "none", minHeight: 40, padding: "0 6px 0 12px", margin: "-12px -4px -10px 0", fontSize: 14, fontWeight: 700, color: "var(--accent-ink)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <I.plus size={15} sw={2.4} />{tk("pickNew")}
                </button>
              )}
            </div>
            <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">{tk("pickNone")}</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

      </div>
      {scan && <QrScanModal onClose={() => setScan(false)} onCode={(c) => { setCode(c); setScan(false); onToast(tm("pairing.codeScanned", { code: c })); }} />}
      <div style={{ padding: "10px 18px 18px", borderTop: "1px solid var(--border)" }}>
        <button className="btn btn--primary btn--lg" disabled={!full || submitting} style={{ opacity: full && !submitting ? 1 : 0.4 }}
          onClick={submit}>
          {submitting ? tm("pairing.registering") : tm("pairing.register")}
        </button>
      </div>
    </div>
  );
}

/* ---------- Load-playlist sheet ---------- */
/* 지금 이 기기에 걸려 있는 재생목록 표시 — '현재' 배지 대신 행 tint(GlassRow active)와 한 세트로 쓰는 사운드바.
   배지는 목록을 훑을 때 놓치기 쉽고, 칠해진 행 + 움직이는 막대는 형태로 읽힌다. */
function NowBars() {
  const tm = useTranslations("control.media");
  return (
    <span className="ctl-eq" aria-label={tm("misc.nowPlaying")} title={tm("misc.nowPlaying")}>
      <i /><i /><i />
    </span>
  );
}

/* ⭐ 2026-09-04 이 시트가 **재생목록을 고르는 정본 얼개**다 — 숙제 내기도 이 시트를 그대로 쓴다(title·currentId·clearRow).
   🛑 재생목록 고르기를 다른 모양(ActionSheet 큰 상자·별도 목록)으로 새로 그리지 말 것 — 두 번 반려됐다
      ("기존에 만들어 둔 시트 템플릿이 있을 거 아니야, 거기에 맞춰"). */
export function LoadSheet({ device, headerSub, title, currentId, status, onPick, onClose, onCreate, masterForms, onPlayMaster, onPlaySetMaster, onPlayMachiSetMaster, onPickVideo, recentCasts, onPickRecent }: { device?: DeviceView; headerSub?: string;
  /** 제목(기본 '무엇을 틀까요?') — 숙제 내기 등 다른 용도로 쓸 때. */
  title?: string;
  /** '지금 걸려 있는 것'을 기기 재생 상태 대신 밖에서 준다(null=없음). 주면 표식은 사운드바가 아니라 체크. */
  currentId?: string | null;
  /** 제목 밑 한 줄 상태(지금 숙제 · OO) + 오른쪽 파란 글자 버튼(숙제 지우기). onClear 없으면 글자만.
      🛑 전엔 '숙제 없음'을 큰 GlassRow 한 줄로 세웠다 — "너무 거대해"(2026-09-04) → 한 줄 상태로. */
  status?: { text: string; clearLabel?: string; onClear?: () => void };
  onPick: (id: string) => void; onClose: () => void; onCreate?: () => void; masterForms?: MasterForm[]; onPlayMaster?: (v: MasterPlayVideo) => void; onPlaySetMaster?: (playlistId: string) => void; onPlayMachiSetMaster?: (videos: MasterPlayVideo[], title: string, startIndex?: number, practice?: DrillPlayOpt | null) => void; onPickVideo?: (v: VideoView) => void; recentCasts?: RecentCast[]; onPickRecent?: (r: RecentCast) => void }) {
  const tm = useTranslations("control.media");
  const segRef = useSegRefLabel();
  const locale = useLocale();
  const { playlists, byId, plById, videos } = useData();
  const mineVideos = videos.filter((v) => !isReferenceVideo(v)); // '내 자료' 개별 영상 캐스팅(한 편씩)
  const [drillForm, setDrillForm] = useState<MasterForm | null>(null); // 품새 누르면 시트 안에서 전체/마디별 뎁스
  // 재생목록 탭 → 마스터 표준 품새면 시트 안에서 드릴(전체/마디별 선택), 아니면 바로 불러오기.
  // ⚠️ 도장 재생목록 id ≠ 마스터 도장 재생목록 id(도장별 시드라 별개)라 playlistId 로만 비교하면 절대 안 맞음
  //    → 라이브러리 탭(LibraryScreen.openPl)과 동일하게 이름으로도 매칭해야 드릴이 뜬다.
  const pickPl = (id: string) => {
    const pl = playlists.find((p) => p.id === id);
    const f = onPlayMaster && onPlaySetMaster
      ? masterForms?.find((m) => m.playlistId === id || (pl && m.name === pl.name))
      : undefined;
    if (f) setDrillForm(f); else onPick(id);
  };
  // 지금 걸려 있는 것 — 밖에서 주면(숙제) 그것, 아니면 기기가 재생 중인 재생목록.
  const curId = currentId !== undefined ? currentId : device?.playlistId;
  const nowMark = currentId !== undefined
    ? <I.check size={18} sw={2.4} style={{ color: "var(--accent)", flex: "none" }} />
    : <NowBars />;
  // 현재 재생 중인 재생목록이 속한 쪽을 처음에 보여준다 (일괄 모드 등 device 없으면 기본 탭)
  const curPl = curId ? playlists.find((p) => p.id === curId) : undefined;
  const [seg, setSeg] = useState<"default" | "mine">(curPl && !curPl.isDefault ? "mine" : "default");
  // 기본 탭엔 '기본동작'·'태극' 묶음 카드 + 나머지 품새동작, 묶음 진입 시 그 안의 목록
  const [group, setGroup] = useState<null | "basic" | "taegeuk">(null); // 항상 최상위에서 시작 — 재생 중이어도 묶음 안으로 점프하지 않음
  const basics = showsBasics(locale) ? playlists.filter(isBasic) : []; // 영문 UI = 기본동작 숨김
  const taegeuks = playlists.filter(isTaegeuk).sort((a, b) => taegeukNo(a.name) - taegeukNo(b.name));
  const shown = seg === "default"
    ? (group === "basic" ? basics
      : group === "taegeuk" ? taegeuks
      : playlists.filter((pl) => pl.isDefault && !isBasic(pl) && !isTaegeuk(pl)))
    : playlists.filter((pl) => !pl.isDefault);
  // 섹션 헤더에 붙는 개수 — 최상위 기본 탭은 묶음 카드(기본동작·태극)도 한 줄씩 세므로 목록에 보이는 줄 수와 같다.
  const listCount = seg === "mine" || group
    ? shown.length
    : shown.length + (basics.length > 0 ? 1 : 0) + (taegeuks.length > 0 ? 1 : 0);
  if (drillForm && onPlayMaster && onPlaySetMaster) {
    return (
      <SheetShell onClose={onClose} height="80%">
        <div className="col" style={{ height: "100%", minHeight: 0 }}>
          <FormDrill form={drillForm} onPlay={onPlayMaster} onPlaySet={onPlaySetMaster} onPlayMachiSet={onPlayMachiSetMaster} onBack={() => setDrillForm(null)} addedPlay={addedPlaySet(drillForm.playlistId, plById, byId)} />
        </div>
      </SheetShell>
    );
  }
  return (
    <SheetShell onClose={onClose} height="80%">
        <div className="sec-head" style={{ marginBottom: 14 }}>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{title ?? tm("load.title")}</span>
          <span className="muted" style={{ fontSize: 13 }}>{headerSub ?? device?.name}</span>
        </div>
        {status && (
          <div className="row" style={{ alignItems: "center", justifyContent: "space-between", gap: 8, margin: "-6px 2px 12px 2px", minHeight: 28 }}>
            <span className="muted" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{status.text}</span>
            {status.onClear && status.clearLabel && (
              <button className="sec-action" onClick={status.onClear} style={{ flex: "none" }}>
                <I.x size={14} sw={2.4} />{status.clearLabel}
              </button>
            )}
          </div>
        )}
        {/* 최근 캐스트 칩 — 지난번 튼 드릴/재생목록/영상 1탭 재적용(라이브 반복 캐스트 2탭) */}
        {recentCasts && recentCasts.length > 0 && onPickRecent && (
          <div className="col gap-6" style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--text-3)", letterSpacing: ".02em" }}>{tm("load.recent")}</span>
            <div className="row gap-8" style={{ overflowX: "auto", paddingBottom: 2 }}>
              {recentCasts.map((r, i) => (
                <button key={i} onClick={() => onPickRecent(r)} className="row gap-6" style={{
                  flex: "none", background: "var(--accent-soft)", color: "var(--accent-ink)", border: "1px solid var(--accent-line)",
                  borderRadius: 999, padding: "7px 13px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                  alignItems: "center", whiteSpace: "nowrap", maxWidth: 200,
                }}>
                  <I.refresh size={14} style={{ flex: "none" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{tkdPlaylist(tkdTitle(r.label, locale), locale)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <Segmented value={seg} onChange={(v) => { setSeg(v); setGroup(null); }} options={[
            { value: "default", label: segRef },
            { value: "mine", label: tm("load.myPlaylists") },
          ]} />
        </div>
        {/* data-tour — 사용법 둘러보기가 '무엇을 틀까요' 목록을 잡는 표식(components/tour.tsx).
            ⚠️ 대기 중인 기기를 누르면 제어 화면을 건너뛰고 이 시트가 바로 열린다. 안내도 그 흐름을 따라간다.
            🛑 control-screen.tsx 안에도 비슷하게 생긴 목록이 있다 — 거기 붙였다가 안내가 영영 대상을
               못 찾고 갇혔다(2026-08-05). '무엇을 틀까요?'를 그리는 건 여기 LoadSheet 다. */}
        <div data-tour="pick-sheet" className="col gap-12">
          {/* 섹션 헤더 — 왼쪽은 '무엇을 고르는 목록인지'(개수 포함), 오른쪽은 보조 액션.
              '만들기'는 예전엔 리스트 위 풀폭 버튼이었는데, 리스트 셀과 같은 흰 카드·같은 폭이라
              목록의 첫 항목처럼 읽히고 시선을 먼저 가져갔다(이 화면의 1순위는 '고르기'). 파란 글자로 물린다. */}
          {(seg === "mine" || !group) && (
            <div className="row" style={{ alignItems: "center", justifyContent: "space-between", gap: 8, margin: "-2px 2px -4px 6px" }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--text-3)", letterSpacing: ".04em" }}>
                {seg === "mine" ? tm("load.myPlaylists") : segRef}
                {listCount > 0 && ` · ${listCount}`}
              </span>
              {seg === "mine" && onCreate && (
                <button className="sec-action" onClick={onCreate}>
                  <I.plus size={15} sw={2.4} />{tm("common.create")}
                </button>
              )}
            </div>
          )}
          {seg === "default" && group && (
            <button onClick={() => setGroup(null)} className="row gap-8" style={{
              background: "none", border: "none", padding: "2px 2px 4px", cursor: "pointer",
              color: "var(--text-1)", fontSize: 15, fontWeight: 700, width: "fit-content",
            }}>
              <I.back size={18} className="dim" />{group === "basic" ? tm("library.basicGroup") : tm("library.taegeukGroup")}
            </button>
          )}

          {((seg === "default" && !group && (basics.length > 0 || taegeuks.length > 0)) || shown.length > 0) && (
            <GlassGroup glass inset={97}>
              {seg === "default" && !group && basics.length > 0 && (
                <GlassRow key="basic"
                  thumb={byId[basics[0].items[0]] ? <Poster video={byId[basics[0].items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />}
                  title={tm("library.basicGroup")}
                  sub={<span style={{ fontSize: 12.5, color: "var(--text-2)" }}>{tm("library.basicGroupSubN", { count: basics.length })}</span>}
                  active={basics.some((p) => p.id === curId)}
                  trailing={basics.some((p) => p.id === curId) ? nowMark : <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
                  onClick={() => setGroup("basic")} />
              )}
              {seg === "default" && !group && taegeuks.length > 0 && (
                <GlassRow key="taegeuk"
                  thumb={byId[taegeuks[0].items[0]] ? <Poster video={byId[taegeuks[0].items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />}
                  title={tm("library.taegeukGroup")}
                  sub={<span style={{ fontSize: 12.5, color: "var(--text-2)" }}>{tm("library.taegeukGroupSubN", { count: taegeuks.length })}</span>}
                  active={taegeuks.some((p) => p.id === curId)}
                  trailing={taegeuks.some((p) => p.id === curId) ? nowMark : <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
                  // data-tour — 자동 시연 코스①이 '태극'으로 들어가 1장을 트는 길목.
                  tour="sheet-taegeuk"
                  onClick={() => setGroup("taegeuk")} />
              )}
              {shown.map((pl, plIdx) => {
                const isCur = curId === pl.id;
                return (
                  <GlassRow key={pl.id}
                    // 🛑 자동 시연이 집는 '바로 틀리는 첫 줄'. 위쪽 묶음 줄(기본동작·태극)은 눌러도
                    //    한 단계 들어갈 뿐이고, 그 안 첫 줄은 '뒤로'라서 자동이 들락날락만 한다
                    //    (2026-08-07 실측). 그래서 드릴 없이 바로 재생되는 줄에만 표식을 단다.
                    tour={plIdx === 0 ? "sheet-play" : undefined}
                    thumb={byId[pl.items[0]] ? <Poster video={byId[pl.items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.layers size={20} />}
                    title={tkdPlaylist(pl.name, locale)}
                    sub={<span style={{ fontSize: 12.5, color: "var(--text-2)" }}>{tm("count.videosDur", { count: pl.items.length, dur: fmt(playlistDuration(pl, byId)) })}</span>}
                    active={isCur}
                    trailing={isCur ? nowMark : <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
                    onClick={() => pickPl(pl.id)} />
                );
              })}
            </GlassGroup>
          )}

          {shown.length === 0 && (seg === "mine" ? mineVideos.length === 0 : (basics.length === 0 && taegeuks.length === 0)) && (
            <div className="muted" style={{ fontSize: 13, padding: "8px 2px" }}>
              {seg === "mine" ? tm("load.noMyContent") : tm("load.noDefault")}
            </div>
          )}

          {/* '내 자료' 개별 영상 — 재생목록에 담지 않고 한 편 바로 캐스팅(LOAD_MASTER) */}
          {seg === "mine" && onPickVideo && mineVideos.length > 0 && (
            <div className="col gap-8">
              <SecLabel>{tm("load.videosOneByOne")}</SecLabel>
              <GlassGroup glass inset={97}>
                {mineVideos.map((v) => {
                  const isCur = device?.videoId === v.id;
                  // 잘린 업로드(complete===false)·비정상(status)은 캐스트 차단 — 재생목록 경로와 동일 가드
                  const disabled = v.status !== "READY" || v.complete === false;
                  return (
                    <GlassRow key={v.id}
                      thumb={<Poster video={v} w="100%" h="100%" radius={0} showLabel={false} showDur={false} />}
                      title={tkdTitle(v.title, locale)}
                      sub={<span style={{ fontSize: 12.5, color: "var(--text-2)" }}>{fmt(v.durationSec)}{disabled ? tm("load.preparing") : ""}</span>}
                      trailing={isCur ? <span className="chip chip--accent" style={{ height: 24 }}>{tm("load.now")}</span> : <I.play size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
                      onClick={disabled ? undefined : () => onPickVideo(v)}
                      style={disabled ? { opacity: 0.5, cursor: "default" } : undefined} />
                  );
                })}
              </GlassGroup>
            </div>
          )}
        </div>
    </SheetShell>
  );
}
